<script>
import { defineAsyncComponent, useTemplateRef, computed, ref } from 'vue';
import { mapGetters, useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useKaptionAIFeaturesPreferences } from 'dashboard/composables/useKaptionAIFeaturesPreferences';
// NOTICE: KAPTION-INTEGRATION - Import undo send event constant
import { UNDO_RESTORE_CONTENT_EVENT } from 'dashboard/composables/useUndoSend';
// NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging
import { useKaptionMessageMerge } from 'dashboard/composables/useKaptionMessageMerge';
import {
  isKaptionMergeableFragment,
  KAPTION_MERGE_FLUSH_REASONS,
} from 'dashboard/helper/kaptionMessageMerge';
import KaptionPendingSendStrip from 'dashboard/components/kaption/KaptionPendingSendStrip.vue';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';

import CannedResponse from './CannedResponse.vue';
import ReplyToMessage from './ReplyToMessage.vue';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
// NOTICE: KAPTION-INTEGRATION
import LiveReplyTranslation from './LiveReplyTranslation.vue';
import KaptionChatSuggestions from './KaptionChatSuggestions.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import MessageSignatureMissingAlert from './MessageSignatureMissingAlert.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
// NOTICE: KAPTION-INTEGRATION - ai-takeover: notes-only while the assistant works + one-click takeover
import KaptionAiTakeoverStrip from './KaptionAiTakeover/KaptionAiTakeoverStrip.vue';
import { kaptionHandlerOf } from 'dashboard/store/modules/conversations/helpers';
// NOTICE: KAPTION-INTEGRATION - Import utility template component for follow-up mode
import { KaptionUtilityTemplateReplyBox } from './KaptionUtilityTemplate';
import KaptionUtilityTemplatesAPI from 'dashboard/api/kaptionUtilityTemplates';
// NOTICE: KAPTION-INTEGRATION - Import scheduled messages API
import KaptionScheduledMessagesAPI from 'dashboard/api/kaptionScheduledMessages';
// NOTICE: KAPTION-INTEGRATION - "Plantilla" button (KAPTION_REPLYBOX_TEMPLATE_PICKER)
import KaptionReplyTemplateModal from './KaptionReplyTemplate/KaptionReplyTemplateModal.vue';
import KaptionPickedTemplateEditor from './KaptionReplyTemplate/KaptionPickedTemplateEditor.vue';
import {
  REPLY_TEMPLATE_FLAG,
  buildPickedTemplateMessagePayload,
  buildPickedTemplateSchedulePayload,
  isTemplatePending,
  isTemplateCapableInbox,
  shouldShowReplyTemplateButton,
} from './KaptionReplyTemplate/kaptionReplyTemplateHelpers';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import CannedResponseBrowser from './CannedResponseBrowser/Index.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
// NOTICE: KAPTION-INTEGRATION - usage-analytics.SEND.1
import { trackUsage } from 'dashboard/composables/useUsageAnalytics';
import { USAGE_ANALYTICS_EVENTS } from 'dashboard/helper/usageAnalytics/constants';
import {
  lengthBucket,
  textHash,
} from 'dashboard/helper/usageAnalytics/constants';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  replaceSignature,
  extractTextFromMarkdown,
} from 'dashboard/helper/editorHelper';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
// NOTICE: KAPTION-INTEGRATION - Conversation-native WhatsApp aggregation.
import * as KaptionMessageBatchesAPI from 'dashboard/api/kaptionMessageBatches';
import * as KaptionContactInfoRequestsAPI from 'dashboard/api/kaptionContactInfoRequests';
import {
  KAPTION_DELIVERY_MODES,
  getKaptionDeliveryModeConfig,
  isKaptionAggregationEligible,
  resolveKaptionDeliveryMode,
} from 'dashboard/helper/kaptionDeliveryMode';
// NOTICE: KAPTION-INTEGRATION - whatsapp-bsuid-coexistence.PHONE_REQUEST.1
import { applyRoleFilter } from 'dashboard/store/modules/conversations/helpers';
import {
  allowedTemplateLanguages as getAllowedTemplateLanguages,
  defaultTemplateLanguage,
} from 'dashboard/helper/kaptionTemplateLanguages';
import {
  getUserPermissions,
  getUserRole,
} from 'dashboard/helper/permissionsHelper';
const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    CannedResponse,
    ReplyBoxBanner,
    // NOTICE: KAPTION-INTEGRATION - ai-takeover
    KaptionAiTakeoverStrip,
    EmojiInput,
    LiveReplyTranslation,
    KaptionChatSuggestions,
    MessageSignatureMissingAlert,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    ResizableTextArea,
    ContentTemplates,
    WhatsappTemplates,
    CannedResponseBrowser,
    WootMessageEditor,
    // NOTICE: KAPTION-INTEGRATION - Follow-up mode component
    KaptionUtilityTemplateReplyBox,
    // NOTICE: KAPTION-INTEGRATION - reply-template-picker
    KaptionReplyTemplateModal,
    KaptionPickedTemplateEditor,
    // NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging
    KaptionPendingSendStrip,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  props: {
    popOutReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:popOutReplyBox'],
  setup() {
    const {
      uiSettings,
      updateUISettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
    } = useUISettings();

    // NOTICE: KAPTION-INTEGRATION
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
    // NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
    const { canUseFeature } = useFeaturePermissions();
    const store = useStore();

    // Create reactive conversationId ref for preferences composable
    const conversationIdRef = computed(() => {
      const currentChat = store.getters.getSelectedChat;
      return currentChat?.id;
    });

    // NOTICE: KAPTION-INTEGRATION - Use same composable as other components
    const {
      translationEnabled,
      suggestionsEnabled,
      setTranslation,
      setSuggestions,
    } = useKaptionAIFeaturesPreferences(conversationIdRef);

    const replyEditor = useTemplateRef('replyEditor');
    // NOTICE: KAPTION-INTEGRATION - Ref for utility template component
    const utilityTemplateRef = ref(null);
    // NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging
    const kaptionMerge = useKaptionMessageMerge();

    return {
      kaptionMerge,
      uiSettings,
      updateUISettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      isKaptionFlagEnabled,
      canUseFeature,
      translationEnabled,
      suggestionsEnabled,
      setTranslation,
      setSuggestions,
      replyEditor,
      utilityTemplateRef,
    };
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.4
      // Stash for an AI suggestion that was inserted into the composer, so a
      // subsequent send can emit suggestion_sent + set had_ai_suggestion_inserted
      // on message_sent. LENGTH/HASH ONLY — never the suggestion text.
      // Shape: { suggestionId, model, length, hash, acceptedAt } | null
      usageInsertedSuggestion: null,
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      isUploading: false,
      replyType: REPLY_EDITOR_MODES.REPLY,
      mentionSearchKey: '',
      hasSlashCommand: false,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      showCannedResponseBrowser: false,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      // NOTICE: KAPTION-INTEGRATION - Track if we switched to Private Note via @ press
      switchedViaAtPress: false,
      // NOTICE: KAPTION-INTEGRATION - Edit message mode state
      isEditingMessage: false,
      editingMessageId: null,
      editingMessageOriginalContent: '',
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      // NOTICE: KAPTION-INTEGRATION - Follow-up mode data
      kaptionUtilityTemplates: {},
      selectedFollowupTemplate: null,
      followupVariable: '',
      templateAttachmentHash: null,
      templateAttachment: null,
      isLoadingTemplates: false,
      showTemplateSearch: false,
      followupLanguage: 'en',
      followupTemplateType: 'followup', // Current template type: 'followup', 'scheduled', 'reminder'
      // NOTICE: KAPTION-INTEGRATION - WhatsApp 24h window expiry timer
      whatsAppWindowTimerId: null,
      // NOTICE: KAPTION-INTEGRATION - reply-template-picker: the account template
      // picked with the "Plantilla" button and its filled values.
      pickedTemplate: null,
      pickedTemplateState: {
        bodyValues: {},
        headerMedia: {},
        buttonValues: {},
        isValid: false,
      },
      showReplyTemplateModal: false,
      isSendingPickedTemplate: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
    }),
    currentContact() {
      if (!this.currentChat?.meta?.sender?.id) return {};
      return this.$store.getters['contacts/getContact'](
        this.currentChat.meta.sender.id
      );
    },
    // NOTICE: KAPTION-INTEGRATION - whatsapp-bsuid-coexistence.PHONE_REQUEST.1
    phoneRequestFeatureEnabled() {
      return this.isKaptionFlagEnabled('KAPTION_WHATSAPP_REQUEST_CONTACT_INFO');
    },
    phoneRequestOutboundRecipientEnabled() {
      return this.isKaptionFlagEnabled(
        'KAPTION_WHATSAPP_BSUID_OUTBOUND_RECIPIENT'
      );
    },
    phoneRequestHasBsuidAlias() {
      const bsuidPattern = /^[A-Z]{2}\.(?:ENT\.)?[A-Za-z0-9]{1,128}$/;
      return (this.currentContact?.contact_inboxes || []).some(
        contactInbox =>
          Number(contactInbox?.inbox?.id) ===
            Number(this.currentChat?.inbox_id) &&
          bsuidPattern.test(contactInbox?.source_id || '')
      );
    },
    phoneRequestCanSendMessages() {
      if (!this.currentChat?.id || !this.currentChat?.meta) return false;

      const permissions = getUserPermissions(
        this.currentUser,
        this.currentAccountId
      );
      const role = getUserRole(this.currentUser, this.currentAccountId);
      const account = this.getAccount(this.currentAccountId);
      const assignmentConsideration =
        account?.settings?.assignment_consideration || 'person';

      return applyRoleFilter(
        this.currentChat,
        role,
        permissions,
        this.currentUser.id,
        assignmentConsideration
      );
    },
    shouldShowReplyToMessage() {
      // NOTICE: KAPTION-INTEGRATION - Allow reply-to banner for private notes on Twilio
      if (this.isPrivate && this.isATwilioChannel) {
        return !!this.inReplyTo?.id;
      }
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel
      );
    },
    showRichContentEditor() {
      if (this.isOnPrivateNote || this.isRichEditorEnabled) {
        return true;
      }

      if (this.isAPIInbox) {
        const {
          display_rich_content_editor: displayRichContentEditor = false,
        } = this.uiSettings;
        return displayRichContentEditor;
      }

      // NOTICE: KAPTION-INTEGRATION - WhatsApp always uses rich editor for formatting
      if (this.isAWhatsAppChannel) {
        return true;
      }

      return false;
    },
    // NOTICE: KAPTION-INTEGRATION - Whether to show formatting toolbar toggle button
    // Shows whenever rich editor is active (email, notes, WhatsApp, etc.)
    showFormattingToolbarToggle() {
      // Don't show in follow-up or schedule mode (they use templates)
      if (this.isOnFollowupMode || this.isOnScheduleMode) {
        return false;
      }
      // Show whenever rich editor is being used
      return this.showRichContentEditor;
    },
    // NOTICE: KAPTION-INTEGRATION - Whether formatting toolbar is visible
    isFormattingToolbarVisible() {
      const { formatting_toolbar_visible: formattingToolbarVisible = false } =
        this.uiSettings;
      return formattingToolbarVisible;
    },
    // NOTICE: KAPTION-INTEGRATION - Hide menu bar unless toolbar is toggled on
    // Applies to all rich editor cases (email, notes, WhatsApp, etc.)
    shouldHideEditorMenuBar() {
      // Only hide if rich editor is shown and toolbar toggle is off
      return this.showRichContentEditor && !this.isFormattingToolbarVisible;
    },
    // NOTICE: KAPTION-INTEGRATION - Hide WhatsApp templates button
    // These channels now use the Schedule tab with utility templates instead
    // feature-permissions.REPLY_BOX.3
    showWhatsappTemplates() {
      return false;
    },
    // NOTICE: KAPTION-INTEGRATION - Hide Content templates button for Twilio
    // These channels now use the Schedule tab with utility templates instead
    showContentTemplates() {
      return false;
    },
    isPrivate() {
      if (this.currentChat.can_reply || this.isAWhatsAppChannel) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    // NOTICE: KAPTION-INTEGRATION - reply-template-picker.BUTTON.1
    isReplyTemplatePickerEnabled() {
      return (
        this.isKaptionFlagEnabled(REPLY_TEMPLATE_FLAG) &&
        this.canUseFeature('whatsapp_templates') &&
        isTemplateCapableInbox(this.inbox)
      );
    },
    showReplyTemplateButton() {
      return shouldShowReplyTemplateButton({
        flagEnabled: this.isKaptionFlagEnabled(REPLY_TEMPLATE_FLAG),
        canUseTemplates: this.canUseFeature('whatsapp_templates'),
        inbox: this.inbox,
        mode: this.replyType,
        isEditingMessage: this.isEditingMessage,
        isReplyRestricted: this.isReplyRestricted,
      });
    },
    // A picked template takes over the box in Responder / Seguimiento /
    // Programar; it is kept (not shown) while the agent writes a private note.
    isPickedTemplateActive() {
      return (
        !!this.pickedTemplate &&
        this.isReplyTemplatePickerEnabled &&
        !this.isOnPrivateNote &&
        !this.isEditingMessage
      );
    },
    isPickedTemplatePending() {
      return isTemplatePending(this.pickedTemplate);
    },
    pickedTemplateContactName() {
      return (
        this.currentChat?.meta?.sender?.name ||
        this.currentChat?.meta?.sender?.phone_number ||
        ''
      );
    },
    // NOTICE: KAPTION-INTEGRATION - whatsapp-automatic-history-sync.REPLY_WINDOW.2
    isWhatsAppReplyWindowClosed() {
      return this.isAWhatsAppChannel && this.currentChat?.can_reply === false;
    },
    isWhatsAppFreeformReplyBlocked() {
      return (
        this.isWhatsAppReplyWindowClosed &&
        !this.isOnPrivateNote &&
        !this.isOnFollowupMode &&
        !this.isOnScheduleMode &&
        !this.isEditingMessage
      );
    },
    isReplyRestricted() {
      if (this.isAWhatsAppChannel) {
        return this.isWhatsAppReplyWindowClosed && !this.isFollowupAvailable;
      }
      return !this.currentChat?.can_reply;
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    // NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging: WhatsApp Cloud
    // only, and only while the flag is on and the agent's level merges.
    isKaptionMergeActive() {
      return (
        this.isAWhatsAppCloudChannel &&
        !this.isOnPrivateNote &&
        this.kaptionMerge.isMerging.value
      );
    },
    isKaptionMergeLevelOn() {
      return this.kaptionMerge.isMerging.value;
    },
    kaptionMergeGroup() {
      if (!this.isAWhatsAppCloudChannel || !this.currentChat?.id) return null;
      return this.kaptionMerge.groupFor(this.currentChat.id);
    },
    // Enter / send with an EMPTY composer sends the held texts now.
    isKaptionMergeFlushGesture() {
      return (
        !!this.kaptionMergeGroup &&
        !this.isOnPrivateNote &&
        !this.isOnFollowupMode &&
        !this.isOnScheduleMode &&
        !this.isEditingMessage &&
        !this.isPickedTemplateActive &&
        this.isMessageEmpty &&
        !this.hasAttachments &&
        !this.hasRecordedAudio
      );
    },
    isReplyButtonDisabled() {
      if (this.isATwitterInbox || this.isWhatsAppFreeformReplyBlocked)
        return true;
      // NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging
      if (this.isKaptionMergeFlushGesture) return false;

      // NOTICE: KAPTION-INTEGRATION - reply-template-picker.SEND.3
      if (this.isPickedTemplateActive) {
        return (
          !this.pickedTemplateState.isValid || this.isSendingPickedTemplate
        );
      }

      // NOTICE: KAPTION-INTEGRATION - Handle follow-up mode send button state
      if (this.isOnFollowupMode) {
        // In follow-up mode, button is enabled when template is selected and variable is filled
        return (
          !this.selectedFollowupTemplate ||
          !this.followupVariable?.trim() ||
          this.isLoadingTemplates
        );
      }

      // NOTICE: KAPTION-INTEGRATION - Handle schedule mode send button state
      if (this.isOnScheduleMode) {
        // In schedule mode, button is enabled when template is selected and variable is filled
        return (
          !this.selectedFollowupTemplate ||
          !this.followupVariable?.trim() ||
          this.isLoadingTemplates
        );
      }

      // NOTICE: KAPTION-INTEGRATION
      // Disable send button while live translation is in progress or waiting
      // This prevents sending before translation is ready
      const liveTranslationRef = this.$refs.liveTranslation;
      if (this.shouldTranslateLiveReply && this.message.trim().length > 0) {
        // Block send if translation is being processed or waiting for translation
        if (
          liveTranslationRef?.isTranslating ||
          liveTranslationRef?.isWaitingForTranslation
        ) {
          return true;
        }
        // Block send if we have text but no translation yet (waiting for first translation)
        if (!liveTranslationRef?.isTranslationReady) {
          return true;
        }
      }

      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel
      );
    },
    replyButtonLabel() {
      if (this.isWhatsAppFreeformReplyBlocked) {
        return this.$t('CONVERSATION.REPLYBOX.WHATSAPP_REPLY_UNAVAILABLE');
      }
      // NOTICE: KAPTION-INTEGRATION - Handle edit mode button label
      if (this.isEditingMessage) {
        return this.$t('CONVERSATION.EDIT_MESSAGE.SAVE');
      }
      // NOTICE: KAPTION-INTEGRATION - reply-template-picker.SEND.2
      if (this.isPickedTemplateActive && !this.isOnScheduleMode) {
        return this.isPickedTemplatePending
          ? this.$t('KAPTION_REPLY_TEMPLATE.SEND_WHEN_APPROVED')
          : this.$t('CONVERSATION.REPLYBOX.SEND');
      }
      // NOTICE: KAPTION-INTEGRATION - Handle follow-up and schedule mode button labels
      // Keyboard shortcuts removed from label - shown in tooltip instead
      if (this.isOnFollowupMode) {
        if (this.isLoadingTemplates) {
          return this.$t('KAPTION_UTILITY_TEMPLATES.LOADING', 'Loading...');
        }
        return this.$t('CONVERSATION.REPLYBOX.SEND');
      }
      // NOTICE: KAPTION-INTEGRATION - Schedule mode shows "Schedule" button
      if (this.isOnScheduleMode) {
        // NOTICE: KAPTION-INTEGRATION - a picked template never waits on wrappers
        if (this.isLoadingTemplates && !this.isPickedTemplateActive) {
          return this.$t('KAPTION_UTILITY_TEMPLATES.LOADING', 'Loading...');
        }
        return this.$t('CONVERSATION.REPLYBOX.SCHEDULE', 'Schedule');
      }

      // NOTICE: KAPTION-INTEGRATION
      // Show messages based on translation state
      const liveTranslationRef = this.$refs.liveTranslation;
      if (this.shouldTranslateLiveReply && this.message.trim().length > 0) {
        if (liveTranslationRef?.isTranslating) {
          return this.$t('CONVERSATION.LIVE_TRANSLATION.TRANSLATING');
        }
        if (liveTranslationRef?.isWaitingForTranslation) {
          return this.$t('CONVERSATION.LIVE_TRANSLATION.WAIT_FOR_TRANSLATION');
        }
        if (!liveTranslationRef?.isTranslationReady) {
          return this.$t('CONVERSATION.LIVE_TRANSLATION.LOADING_TRANSLATION');
        }
      }

      if (this.isPrivate) {
        return this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      return this.$t('CONVERSATION.REPLYBOX.SEND');
    },
    // NOTICE: KAPTION-INTEGRATION - Keyboard shortcut tooltip for send button
    replyButtonTooltip() {
      if (this.isWhatsAppFreeformReplyBlocked) {
        return this.$t('CONVERSATION.REPLYBOX.WHATSAPP_WINDOW_CLOSED');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? '⌘ + Enter'
        : 'Enter';
      return keyLabel;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    isRichEditorEnabled() {
      return this.isAWebWidgetInbox || this.isAnEmailChannel;
    },
    // feature-permissions.REPLY_BOX.4
    showAudioRecorder() {
      return (
        !this.isOnPrivateNote &&
        this.showFileUpload &&
        this.canUseFeature('voice_recording')
      );
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    // NOTICE: KAPTION-INTEGRATION - Client-managed private-note permission.
    canWritePrivateNotes() {
      return this.canUseFeature('private_notes');
    },
    // NOTICE: KAPTION-INTEGRATION - ai-takeover.COMPOSER.1: while the assistant handles the
    // conversation (pending, or assigned to an assistant) people write private notes only and
    // take it over explicitly. Editing a message already sent is left alone.
    isKaptionAssistantLock() {
      return (
        !!this.currentChat?.id &&
        this.isKaptionFlagEnabled('KAPTION_AI_TAKEOVER') &&
        kaptionHandlerOf(this.currentChat) === 'assistant'
      );
    },
    // NOTICE: KAPTION-INTEGRATION - Follow-up mode computed properties
    isOnFollowupMode() {
      return this.replyType === REPLY_EDITOR_MODES.FOLLOWUP;
    },
    // NOTICE: KAPTION-INTEGRATION - Schedule mode computed property
    isOnScheduleMode() {
      return this.replyType === REPLY_EDITOR_MODES.SCHEDULE;
    },
    // NOTICE: KAPTION-INTEGRATION - Check if meta payment is confirmed in inbox settings
    isMetaPaymentConfirmed() {
      return this.inbox?.provider_config?.meta_payment_confirmed === true;
    },
    // NOTICE: KAPTION-INTEGRATION - Whether to show the Schedule tab
    // For WhatsApp Cloud API: requires meta payment confirmation
    // For Twilio WhatsApp: always available (no payment check needed)
    showScheduleTab() {
      if (!this.canUseFeature('scheduled_messages')) {
        return false;
      }
      // Twilio WhatsApp - always show Schedule tab (no payment check)
      if (this.isATwilioWhatsAppChannel) {
        return true;
      }
      // WhatsApp Cloud API - requires meta payment confirmation
      if (this.isAWhatsAppCloudChannel && this.isMetaPaymentConfirmed) {
        return true;
      }
      return false;
    },
    isFollowupAvailable() {
      // Follow-up mode is only available for WhatsApp channels outside 24h window
      // For WhatsApp Cloud API: requires meta payment confirmation
      // For Twilio WhatsApp: always available (no payment check)
      if (!this.isAWhatsAppChannel || this.currentChat?.can_reply) {
        return false;
      }
      // Twilio WhatsApp - no payment check needed
      if (this.isATwilioWhatsAppChannel) {
        return true;
      }
      // WhatsApp Cloud API - requires meta payment confirmation
      return this.isAWhatsAppCloudChannel && this.isMetaPaymentConfirmed;
    },
    // NOTICE: KAPTION-INTEGRATION - Show schedule button when feature flag is enabled
    showScheduleButton() {
      if (!this.canUseFeature('scheduled_messages')) {
        return false;
      }
      if (this.isOnPrivateNote) {
        return false;
      }
      // For WhatsApp Cloud API and Twilio WhatsApp channels:
      // NEVER show the small schedule button - these channels use the Schedule tab instead
      if (this.isAWhatsAppCloudChannel || this.isATwilioWhatsAppChannel) {
        return false;
      }
      // For other channels: show schedule button in Reply mode
      return true;
    },
    // NOTICE: KAPTION-INTEGRATION - Get current message type for scheduling
    scheduleMessageType() {
      return this.isOnFollowupMode || this.isOnScheduleMode
        ? 'template'
        : 'regular';
    },
    // Follow-up template navigation computed properties
    followupTemplateCounter() {
      return this.utilityTemplateRef?.templateCounterText || '';
    },
    // NOTICE: KAPTION-INTEGRATION - Attachment upload state for follow-up mode
    isUploadingAttachment() {
      return this.utilityTemplateRef?.isUploadingAttachment || false;
    },
    canGoPrevFollowupTemplate() {
      return this.utilityTemplateRef?.canGoPrev || false;
    },
    canGoNextFollowupTemplate() {
      return this.utilityTemplateRef?.canGoNext || false;
    },
    followupAvailableLanguages() {
      return (
        this.utilityTemplateRef?.availableLanguages || [this.followupLanguage]
      );
    },
    // NOTICE: KAPTION-INTEGRATION - template-languages.UI.1
    allowedTemplateLanguages() {
      return getAllowedTemplateLanguages(
        this.getAccount(this.currentAccountId)
      );
    },
    // NOTICE: KAPTION-INTEGRATION - Template types the user is permitted to surface.
    // feature-permissions.REPLY_BOX.8 - Drops the 'reminder' type when the
    // reminders feature permission is not granted; followup/scheduled are unaffected.
    permittedTemplateTypes() {
      const types = ['followup', 'scheduled', 'reminder'];
      if (!this.canUseFeature('reminders')) {
        return types.filter(type => type !== 'reminder');
      }
      return types;
    },
    // NOTICE: KAPTION-INTEGRATION - Check if there are ANY templates for the account language
    hasTemplatesInAccountLanguage() {
      if (!this.kaptionUtilityTemplates) return false;
      const lang = this.followupLanguage;
      return this.permittedTemplateTypes.some(type => {
        const typeTemplates = this.kaptionUtilityTemplates[type];
        return typeTemplates?.[lang]?.length > 0;
      });
    },
    // NOTICE: KAPTION-INTEGRATION - Error state when no templates available
    followupNoTemplatesError() {
      if (this.isLoadingTemplates) return false;
      if (Object.keys(this.kaptionUtilityTemplates).length === 0) return false;
      return !this.hasTemplatesInAccountLanguage;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.signatureToApply;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    editorMessageKey() {
      const { editor_message_key: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    commandPlusEnterToSendEnabled() {
      return this.editorMessageKey === 'cmd_enter';
    },
    enterToSendEnabled() {
      return this.editorMessageKey === 'enter';
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      return variables;
    },
    // ensure that the signature is plain text depending on `showRichContentEditor`
    signatureToApply() {
      return this.showRichContentEditor
        ? this.messageSignature
        : extractTextFromMarkdown(this.messageSignature);
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    // NOTICE: KAPTION-INTEGRATION
    // Check if live reply translation should be enabled
    shouldTranslateLiveReply() {
      // Only translate for replies (not private notes)
      if (this.isPrivate) return false;

      // NOTICE: KAPTION-INTEGRATION - Check master toggle from topbar settings first
      // This ensures the feature is completely disabled when turned off in settings
      const masterToggleEnabled =
        this.uiSettings?.kaption_live_translation_enabled ?? true;
      if (!masterToggleEnabled) return false;

      // Get conversation language
      const conversationLanguage =
        this.currentChat?.additional_attributes?.conversation_language;

      // Get account/agent language (from current UI locale)
      const agentLanguage =
        this.$root.$i18n.locale ||
        this.uiSettings?.locale ||
        this.getAccount(this.currentAccountId)?.locale ||
        'en';

      // Don't translate if languages are the same
      if (!conversationLanguage || conversationLanguage === agentLanguage) {
        return false;
      }

      // NOTICE: KAPTION-INTEGRATION - Use composable value instead of direct localStorage access
      // This ensures we're using the same storage key format as KaptionAIFeaturesPill
      return this.translationEnabled;
    },
    liveTranslationSourceLanguage() {
      return (
        this.$root.$i18n.locale ||
        this.uiSettings?.locale ||
        this.getAccount(this.currentAccountId)?.locale ||
        'en'
      );
    },
    liveTranslationTargetLanguage() {
      // NOTICE: KAPTION-INTEGRATION - Always read from server state, never assume default
      // This prevents sync issues where frontend defaults differ from backend detection
      const language =
        this.currentChat?.additional_attributes?.conversation_language;
      if (!language && this.currentChat?.id) {
        // Log when language not yet detected - backend will auto-detect on next message
        // eslint-disable-next-line no-console
        console.debug(
          `[KAPTION] Conversation language not yet detected for chat ${this.currentChat.id}`
        );
      }
      return language;
    },
  },
  watch: {
    // NOTICE: KAPTION-INTEGRATION - ai-takeover.COMPOSER.1 / .COMPOSER.2: lock as soon as the
    // assistant picks the conversation up; unlock into the normal reply mode once a person
    // owns it (this person's takeover, or anyone's).
    isKaptionAssistantLock: {
      handler(isLocked, wasLocked) {
        if (isLocked) this.enterKaptionAssistantLock();
        else if (wasLocked) this.leaveKaptionAssistantLock();
      },
      immediate: true,
    },
    canWritePrivateNotes(canWrite) {
      if (!canWrite && this.isOnPrivateNote && !this.isEditingMessage) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Watch for inbox loading to initialize follow-up mode
    inbox: {
      handler(newInbox) {
        if (newInbox && this.currentChat) {
          // eslint-disable-next-line no-console
          console.log('[KAPTION] inbox loaded', {
            channelType: newInbox.channel_type,
            canReply: this.currentChat.can_reply,
            currentReplyType: this.replyType,
            metaPaymentConfirmed:
              newInbox.provider_config?.meta_payment_confirmed,
          });
          // NOTICE: KAPTION-INTEGRATION - Re-check follow-up mode when inbox becomes available
          // Only enable follow-up mode if meta payment is confirmed
          if (
            // NOTICE: KAPTION-INTEGRATION - ai-takeover.COMPOSER.1
            !this.isKaptionAssistantLock &&
            this.isAWhatsAppChannel &&
            !this.currentChat.can_reply &&
            this.isMetaPaymentConfirmed &&
            this.replyType !== REPLY_EDITOR_MODES.FOLLOWUP
          ) {
            // eslint-disable-next-line no-console
            console.log(
              '[KAPTION] Inbox loaded with payment confirmed, switching to FOLLOWUP mode'
            );
            this.replyType = REPLY_EDITOR_MODES.FOLLOWUP;
            this.fetchKaptionUtilityTemplates();
          }
        }
      },
      immediate: true,
    },
    currentChat(conversation, oldConversation) {
      const { can_reply: canReply } = conversation;
      const isConversationChange =
        oldConversation && oldConversation.id !== conversation.id;

      // NOTICE: KAPTION-INTEGRATION - Load scoped contactable aliases used by
      // request-contact-info eligibility whenever the selected contact changes.
      if (conversation?.meta?.sender?.id) {
        this.$store.dispatch(
          'contacts/fetchContactableInbox',
          conversation.meta.sender.id
        );
      }

      if (isConversationChange && this.isEditingMessage) {
        this.exitEditMode();
      }

      if (isConversationChange) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();

        // NOTICE: KAPTION-INTEGRATION - Apply "Write Notes by Default" setting
        // When switching to a new conversation, check if user prefers to start in private note mode
        if (this.shouldStartWithPrivateNote(canReply)) {
          this.replyType = this.canWritePrivateNotes
            ? REPLY_EDITOR_MODES.NOTE
            : REPLY_EDITOR_MODES.REPLY;
          this.fetchAndSetReplyTo();
          return;
        }
      }

      // NOTICE: KAPTION-INTEGRATION - ai-takeover.COMPOSER.1
      if (this.isKaptionAssistantLock) {
        this.enterKaptionAssistantLock();
        this.fetchAndSetReplyTo();
        return;
      }

      if (this.isOnPrivateNote) {
        return;
      }

      // NOTICE: KAPTION-INTEGRATION - Auto-select Follow-up mode for WhatsApp when can_reply is false
      // AND meta payment is confirmed in inbox settings
      if (this.isAWhatsAppChannel) {
        if (canReply) {
          this.replyType = REPLY_EDITOR_MODES.REPLY;
        } else if (this.isMetaPaymentConfirmed) {
          // WhatsApp 24h window expired + payment confirmed - use Follow-up mode
          this.replyType = REPLY_EDITOR_MODES.FOLLOWUP;
          // Fetch templates if not already loaded
          this.fetchKaptionUtilityTemplates();
        } else {
          // WhatsApp 24h window expired but no payment confirmed - use NOTE mode
          this.replyType = this.canWritePrivateNotes
            ? REPLY_EDITOR_MODES.NOTE
            : REPLY_EDITOR_MODES.REPLY;
        }
      } else if (canReply) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = this.canWritePrivateNotes
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      }

      // NOTICE: KAPTION-INTEGRATION - Restart WhatsApp 24h window expiry check on conversation change
      this.startWhatsAppWindowExpiryCheck();

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      // NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging: leaving the
      // conversation sends what is held there.
      if (conversationId !== oldConversationId && oldConversationId) {
        this.kaptionMerge?.flush(
          Number(oldConversationId),
          KAPTION_MERGE_FLUSH_REASONS.LEAVE
        );
      }
      if (conversationId !== oldConversationId) {
        // NOTICE: KAPTION-INTEGRATION - reply-template-picker.EDITOR.5
        this.clearPickedTemplate();
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
        this.resetRecorderAndClearAttachments();
      }
    },
    message(updatedMessage) {
      // Check if the message starts with a slash.
      const bodyWithoutSignature = removeSignature(
        updatedMessage,
        this.signatureToApply
      );
      const startsWithSlash = bodyWithoutSignature.startsWith('/');

      // Determine if the user is potentially typing a slash command.
      // This is true if the message starts with a slash and the rich content editor is not active.
      this.hasSlashCommand = startsWithSlash && !this.showRichContentEditor;
      this.showMentions = this.hasSlashCommand;

      // If a slash command is active, extract the command text after the slash.
      // If not, reset the mentionSearchKey.
      this.mentionSearchKey = this.hasSlashCommand
        ? bodyWithoutSignature.substring(1)
        : '';

      // Autosave the current message draft.
      this.doAutoSaveDraft();
      // NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging: typing with
      // text in the box extends the hold.
      if (this.kaptionMergeGroup) {
        this.kaptionMerge.noteTyping(
          this.currentChat.id,
          !!updatedMessage?.trim()
        );
      }
    },
    // NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging: the flag or the
    // level turned off mid-hold → send what is held.
    isKaptionMergeLevelOn(isMerging) {
      if (!isMerging) {
        this.kaptionMerge.flushAll(KAPTION_MERGE_FLUSH_REASONS.SETTING);
      }
    },
    replyType(updatedReplyType, oldReplyType) {
      // NOTICE: KAPTION-INTEGRATION - Skip draft save/restore when entering edit mode
      // to prevent overwriting the edit content with a stale draft
      if (this.isEditingMessage) return;
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },

  mounted() {
    this.getFromDraft();
    // NOTICE: KAPTION-INTEGRATION - Hydrate the current inbox alias before
    // evaluating request-contact-info visibility on initial render.
    if (this.currentChat?.meta?.sender?.id) {
      this.$store.dispatch(
        'contacts/fetchContactableInbox',
        this.currentChat.meta.sender.id
      );
    }
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if input/textarea is focussed.
    document.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        // NOTICE: KAPTION-INTEGRATION - Don't save draft while editing a message
        if (this.isEditingMessage) return;
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    // NOTICE: KAPTION-INTEGRATION - Preload canned responses so the browser modal opens instantly
    this.$store.dispatch('getCannedResponse', {});
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    // NOTICE: KAPTION-INTEGRATION - Listen for attachment injection from dashboard apps
    emitter.on(
      BUS_EVENTS.INSERT_ATTACHMENTS_INTO_COMPOSER,
      this.handleAttachmentInjection
    );

    // NOTICE: KAPTION-INTEGRATION
    // Listen for conversation language changes from LiveReplyTranslation
    emitter.on(
      'kaption-conversation-language-changed',
      this.handleConversationLanguageChange
    );
    // NOTICE: KAPTION-INTEGRATION - Listen for translation toggle events
    emitter.on('kaption-translation-toggled', this.handleTranslationToggle);
    // NOTICE: KAPTION-INTEGRATION - Listen for suggestions toggle events
    emitter.on('kaption-suggestions-toggled', this.handleSuggestionsToggle);

    // NOTICE: KAPTION-INTEGRATION - Check for follow-up mode on initial load
    this.initializeFollowupModeIfNeeded();

    // NOTICE: KAPTION-INTEGRATION - Listen for undo restore content events
    emitter.on(UNDO_RESTORE_CONTENT_EVENT, this.handleUndoRestoreContent);
    // NOTICE: KAPTION-INTEGRATION - Listen for edit message events
    emitter.on(BUS_EVENTS.TOGGLE_EDIT_MESSAGE, this.enterEditMode);
    // NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ownership.OUTBOUND.3
    emitter.on('kaption-call-ask-teammate', this.kaptionAskTeammate);

    // NOTICE: KAPTION-INTEGRATION - Start WhatsApp 24h window expiry check
    this.startWhatsAppWindowExpiryCheck();
  },
  unmounted() {
    document.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    // NOTICE: KAPTION-INTEGRATION - Clean up attachment injection listener
    emitter.off(
      BUS_EVENTS.INSERT_ATTACHMENTS_INTO_COMPOSER,
      this.handleAttachmentInjection
    );
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );

    // NOTICE: KAPTION-INTEGRATION
    emitter.off(
      'kaption-conversation-language-changed',
      this.handleConversationLanguageChange
    );
    emitter.off('kaption-translation-toggled', this.handleTranslationToggle);
    emitter.off('kaption-suggestions-toggled', this.handleSuggestionsToggle);
    // NOTICE: KAPTION-INTEGRATION - Clean up undo restore content listener
    emitter.off(UNDO_RESTORE_CONTENT_EVENT, this.handleUndoRestoreContent);
    // NOTICE: KAPTION-INTEGRATION - Clean up edit message listener
    emitter.off(BUS_EVENTS.TOGGLE_EDIT_MESSAGE, this.enterEditMode);
    // NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ownership.OUTBOUND.3
    emitter.off('kaption-call-ask-teammate', this.kaptionAskTeammate);
    // NOTICE: KAPTION-INTEGRATION - Clear WhatsApp 24h window expiry timer
    this.clearWhatsAppWindowExpiryTimer();
  },
  methods: {
    async queueMessageBatch(messagePayload, mode, targetCount) {
      const clientFragmentId =
        window.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const response = await KaptionMessageBatchesAPI.addMessageBatchFragment(
        this.currentAccountId,
        this.currentChat.id,
        {
          mode,
          target_count: targetCount,
          content: messagePayload.content ?? messagePayload.message,
          client_fragment_id: messagePayload.echo_id || clientFragmentId,
        }
      );
      emitter.emit('kaption-message-batch-updated');
      return Boolean(response?.data?.message_id);
    },
    // NOTICE: KAPTION-INTEGRATION - WhatsApp 24h window expiry timer
    // Checks every 5 seconds whether the WhatsApp messaging window has expired
    // by comparing Date.now() against the last incoming message's created_at + 24h.
    // When expired, locally flips can_reply to false so the UI switches to Follow-up mode.
    clearWhatsAppWindowExpiryTimer() {
      if (this.whatsAppWindowTimerId) {
        clearInterval(this.whatsAppWindowTimerId);
        this.whatsAppWindowTimerId = null;
      }
    },
    checkWhatsAppWindowExpiry() {
      const conversation = this.currentChat;
      if (!conversation || !conversation.can_reply) {
        this.clearWhatsAppWindowExpiryTimer();
        return;
      }

      const { messages = [] } = conversation;
      // Find the last incoming message (message_type 0 = INCOMING)
      const lastIncoming = [...messages]
        .reverse()
        .find(m => m.message_type === 0);

      if (!lastIncoming || !lastIncoming.created_at) {
        return;
      }

      // created_at is a Unix timestamp in seconds
      const windowExpiresAt = (lastIncoming.created_at + 24 * 60 * 60) * 1000;
      if (Date.now() >= windowExpiresAt) {
        this.$store.commit('SET_CONVERSATION_CAN_REPLY', {
          conversationId: conversation.id,
          canReply: false,
        });
        this.clearWhatsAppWindowExpiryTimer();
      }
    },
    startWhatsAppWindowExpiryCheck() {
      this.clearWhatsAppWindowExpiryTimer();

      // Only run for WhatsApp conversations where reply is currently allowed
      if (!this.isAWhatsAppChannel || !this.currentChat?.can_reply) {
        return;
      }

      // Run an immediate check, then every 5 seconds
      this.checkWhatsAppWindowExpiry();
      if (this.currentChat?.can_reply) {
        this.whatsAppWindowTimerId = setInterval(
          () => this.checkWhatsAppWindowExpiry(),
          5000
        );
      }
    },

    // NOTICE: KAPTION-INTEGRATION - Check if user prefers to start in private note mode
    shouldStartWithPrivateNote(canReply) {
      // Only apply if conversation can be replied to
      // (don't override NOTE mode for expired WhatsApp windows)
      if (!canReply || !this.canWritePrivateNotes) return false;

      // Check the "Write Notes by Default" setting from ui_settings
      return this.uiSettings?.kaption_write_notes_default === true;
    },
    handleInsert(article) {
      const { url, title } = article;
      if (this.isRichEditorEnabled) {
        // Removing empty lines from the title
        const lines = title.split('\n');
        const nonEmptyLines = lines.filter(line => line.trim() !== '');
        const filteredMarkdown = nonEmptyLines.join(' ');
        emitter.emit(
          BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
          `[${filteredMarkdown}](${url})`
        );
      } else {
        this.addIntoEditor(
          `${this.$t('CONVERSATION.REPLYBOX.INSERT_READ_MORE')} ${url}`
        );
      }

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    onSuggestionSelected(payload) {
      // NOTICE: KAPTION-INTEGRATION - usage-analytics.SUGGESTIONS.4
      // The suggestions child now emits an object { text, suggestionId, model }.
      // Keep backward-compat with the legacy plain-string form.
      const suggestionText =
        typeof payload === 'object' && payload !== null
          ? payload.text
          : payload;

      // Insert suggestion into message input
      this.addIntoEditor(suggestionText);

      // Stash correlation metadata (length + hash only, never the text) so a
      // later send can emit suggestion_sent + edited_before_send.
      try {
        if (
          typeof payload === 'object' &&
          payload !== null &&
          payload.suggestionId
        ) {
          const text = suggestionText || '';
          this.usageInsertedSuggestion = {
            suggestionId: payload.suggestionId,
            model: payload.model ?? null,
            length: text.length,
            hash: textHash(text),
            acceptedAt: Date.now(),
          };
        }
      } catch (e) {
        // best-effort
      }
    },
    toggleRichContentEditor() {
      this.updateUISettings({
        display_rich_content_editor: !this.showRichContentEditor,
      });

      const plainTextSignature = extractTextFromMarkdown(this.messageSignature);

      if (!this.showRichContentEditor && this.messageSignature) {
        // remove the old signature -> extract text from markdown -> attach new signature
        let message = removeSignature(this.message, this.messageSignature);
        message = extractTextFromMarkdown(message);
        message = appendSignature(message, plainTextSignature);

        this.message = message;
      } else {
        this.message = replaceSignature(
          this.message,
          plainTextSignature,
          this.messageSignature
        );
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Toggle formatting toolbar visibility
    toggleFormattingToolbar() {
      this.updateUISettings({
        formatting_toolbar_visible: !this.isFormattingToolbarVisible,
      });
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = `draft-${conversationId}-${replyType}`;
        const draftToSave = trimContent(this.message || '');

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
        });
      }
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      return this.sendWithSignature
        ? appendSignature(message, this.signatureToApply)
        : removeSignature(message, this.signatureToApply);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            if (this.isEditingMessage) {
              this.exitEditMode();
              return;
            }
            // NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging: Esc
            // takes the last held text back into the composer.
            if (this.kaptionMergeGroup) {
              this.undoKaptionMergedFragment();
              return;
            }
            this.hideEmojiPicker();
            this.hideMentions();
            this.showUserMentions = false;
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      const data = e.clipboardData.files;
      if (!this.showRichContentEditor && data.length !== 0) {
        this.$refs.messageInput.$el.blur();
      }
      if (!data.length || !data[0]) {
        return;
      }
      data.forEach(file => {
        const { name, type, size } = file;
        this.onFileUpload({ name, type, size, file: file });
      });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      if (value && !this.canUseFeature('canned_responses')) {
        this.showCannedMenu = false;
        return;
      }
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      if (!this.canUseFeature('whatsapp_templates')) return;
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openCannedResponseBrowser() {
      if (!this.canUseFeature('canned_responses')) return;
      this.showCannedResponseBrowser = true;
    },
    hideCannedResponseBrowser() {
      this.showCannedResponseBrowser = false;
    },
    onInsertCannedResponse(response) {
      this.replaceText({
        text: response.content,
        attachments: response.attachments || [],
      });
      this.hideCannedResponseBrowser();
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    confirmOnSendReply() {
      if (
        this.isReplyButtonDisabled ||
        (this.isPrivate && !this.canWritePrivateNotes)
      ) {
        return;
      }
      if (!this.showMentions) {
        const isOnWhatsApp =
          this.isATwilioWhatsAppChannel ||
          this.isAWhatsAppCloudChannel ||
          this.is360DialogWhatsAppChannel;
        // When users send messages containing both text and attachments on Instagram, Instagram treats them as separate messages.
        // Although Chatwoot combines these into a single message, Instagram sends separate echo events for each component.
        // This can create duplicate messages in Chatwoot. To prevent this issue, we'll handle text and attachments as separate messages.
        const isOnInstagram = this.isAnInstagramChannel;
        if ((isOnWhatsApp || isOnInstagram) && !this.isPrivate) {
          this.sendMessageAsMultipleMessages(this.message);
        } else {
          // NOTICE: KAPTION-INTEGRATION
          // Use translated message if available
          const messagePayload = this.getMessagePayloadWithTranslation(
            this.message
          );
          this.sendMessage(messagePayload);
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.clearMessage();
        this.hideEmojiPicker();
        this.$emit('update:popOutReplyBox', false);
      }
    },
    sendMessageAsMultipleMessages(message) {
      const messages = this.getMultipleMessagesPayloadWithTranslation(message);
      messages.forEach(messagePayload => {
        this.sendMessage(messagePayload);
      });
    },
    sendMessageAnalyticsData(isPrivate, messagePayload = {}) {
      // Analytics data for message signature is enabled or not in channels
      const posthogResult = isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });

      // NOTICE: KAPTION-INTEGRATION - usage-analytics.SEND.1 / NOTE.1 / QUOTE.1 /
      // SUGGESTIONS.4. Emit the product-usage events off the SAME single send
      // funnel. Metadata-only + best-effort; wrapped so it can never break send.
      try {
        this.trackUsageSend(isPrivate, messagePayload);
      } catch (e) {
        // best-effort
      }

      return posthogResult;
    },
    // NOTICE: KAPTION-INTEGRATION - usage-analytics.SEND.1
    trackUsageSend(isPrivate, messagePayload = {}) {
      const contentAttributes = messagePayload.contentAttributes || {};
      const isQuoted = !!contentAttributes.in_reply_to;
      const attachmentCount = Array.isArray(messagePayload.files)
        ? messagePayload.files.length
        : 0;
      const hasAttachment = attachmentCount > 0;
      // message_type: derive a short enum from the composer mode + channel.
      const messageType = isPrivate ? 'note' : 'reply';
      // The composer text length (signature-independent bucketing).
      const composerText = this.message || '';
      const length = composerText.length;

      if (isPrivate) {
        // usage-analytics.NOTE.1 — private note funnel.
        trackUsage(USAGE_ANALYTICS_EVENTS.PRIVATE_NOTE_CREATED, {
          length_bucket: lengthBucket(length),
          has_attachment: hasAttachment,
        });
        return;
      }

      // usage-analytics.QUOTE.1 — reply-to/quote (only when set on send).
      if (isQuoted) {
        trackUsage(USAGE_ANALYTICS_EVENTS.REPLY_QUOTED, {
          quoted: true,
          quoted_message_type: this.inReplyTo?.message_type ?? null,
        });
      }

      // usage-analytics.SUGGESTIONS.4 — did a stashed suggestion get sent?
      const stashed = this.usageInsertedSuggestion;
      let hadSuggestionInserted = false;
      if (stashed && stashed.suggestionId) {
        hadSuggestionInserted = true;
        const editedBeforeSend =
          length !== stashed.length || textHash(composerText) !== stashed.hash;
        trackUsage(USAGE_ANALYTICS_EVENTS.SUGGESTION_SENT, {
          suggestion_id: stashed.suggestionId,
          model: stashed.model ?? null,
          edited_before_send: editedBeforeSend,
          time_accept_to_send_ms: stashed.acceptedAt
            ? Date.now() - stashed.acceptedAt
            : null,
        });
      }

      // usage-analytics.SEND.1 — the message_sent funnel event.
      const liveTranslationRef = this.$refs.liveTranslation;
      const hadLiveTranslation = !!contentAttributes.kaption_translations;
      let liveTranslationProvider = null;
      if (hadLiveTranslation) {
        liveTranslationProvider =
          liveTranslationRef?.translationProvider || 'kaption_live_reply';
      }
      const languagePair = hadLiveTranslation
        ? `${this.liveTranslationSourceLanguage}->${this.liveTranslationTargetLanguage}`
        : null;

      trackUsage(USAGE_ANALYTICS_EVENTS.MESSAGE_SENT, {
        message_type: messageType,
        had_live_translation: hadLiveTranslation,
        live_translation_provider: liveTranslationProvider,
        language_pair: languagePair,
        length_bucket: lengthBucket(length),
        has_attachment: hasAttachment,
        attachment_count: attachmentCount,
        is_quoted_reply: isQuoted,
        signature_enabled: this.sendWithSignature,
        had_ai_suggestion_inserted: hadSuggestionInserted,
      });

      // Consume the stash so it can't correlate to a later, unrelated send.
      this.usageInsertedSuggestion = null;
    },
    async onSendReply() {
      // NOTICE: KAPTION-INTEGRATION - whatsapp-automatic-history-sync.REPLY_WINDOW.2
      if (this.isWhatsAppFreeformReplyBlocked) return;

      // NOTICE: KAPTION-INTEGRATION - Handle edit mode save
      if (this.isEditingMessage) {
        await this.saveEditedMessage();
        return;
      }

      // NOTICE: KAPTION-INTEGRATION - reply-template-picker.SEND.1
      // In Programar the popover is the only way out (Enter must not send now).
      if (this.isPickedTemplateActive) {
        if (!this.isOnScheduleMode) await this.onSendPickedTemplate();
        return;
      }

      // NOTICE: KAPTION-INTEGRATION - Handle follow-up mode send
      if (this.isOnFollowupMode) {
        await this.onSendFollowup();
        return;
      }

      // NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging: Enter (or the
      // send button) with an empty composer sends the held texts now.
      if (this.isKaptionMergeFlushGesture) {
        this.kaptionMerge.flush(
          this.currentChat.id,
          KAPTION_MERGE_FLUSH_REASONS.MANUAL
        );
        return;
      }

      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(messagePayload) {
      // NOTICE: KAPTION-INTEGRATION - Recheck direct sends before batching/undo/API.
      // whatsapp-automatic-history-sync.REPLY_WINDOW.2
      if (
        this.isWhatsAppReplyWindowClosed &&
        !messagePayload.private &&
        !messagePayload.templateParams
      ) {
        useAlert(this.$t('CONVERSATION.REPLYBOX.WHATSAPP_WINDOW_CLOSED'));
        return;
      }
      // NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging. A plain text is
      // held and merged; anything else first sends what is held (and waits for
      // it to be created) so the order the agent typed is kept.
      if (this.isKaptionMergeActive && !messagePayload.private) {
        if (isKaptionMergeableFragment(messagePayload)) {
          this.kaptionMerge.hold(messagePayload);
          emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
          this.removeFromDraft();
          emitter.emit(BUS_EVENTS.MESSAGE_SENT);
          this.sendMessageAnalyticsData(false, messagePayload);
          return;
        }
        await this.kaptionMerge
          .flush(
            messagePayload.conversationId,
            KAPTION_MERGE_FLUSH_REASONS.NON_MERGEABLE
          )
          .catch(() => {});
      }
      try {
        // NOTICE: KAPTION-INTEGRATION - Quantity, not time, determines the
        // Combine modes. Unsupported shapes always use Chatwoot's send path.
        // whatsapp-message-merging: the old quantity batching never runs while
        // KAPTION_MESSAGE_MERGING is on.
        const batchingEnabled =
          this.isKaptionFlagEnabled('KAPTION_MESSAGE_BATCHING') &&
          !this.isKaptionFlagEnabled('KAPTION_MESSAGE_MERGING');
        const deliveryMode = resolveKaptionDeliveryMode(this.uiSettings, {
          batchingEnabled,
        });
        const deliveryConfig = getKaptionDeliveryModeConfig(deliveryMode);
        const shouldUseQuantityBatch =
          this.isAWhatsAppChannel &&
          batchingEnabled &&
          deliveryConfig.aggregate &&
          isKaptionAggregationEligible(messagePayload);
        const shouldUseUndo =
          this.isAWhatsAppChannel &&
          !messagePayload.private &&
          this.canUseFeature('undo_send') &&
          deliveryMode === KAPTION_DELIVERY_MODES.UNDO;

        let wasSent = true;
        if (shouldUseQuantityBatch) {
          wasSent = await this.queueMessageBatch(
            messagePayload,
            deliveryMode,
            deliveryConfig.targetCount
          );
        } else if (shouldUseUndo) {
          // NOTICE: KAPTION-INTEGRATION - Undo lasts at most 15 seconds, so one
          // customer-facing WhatsApp typing signal covers the full pending window.
          this.toggleTyping('on');
          await this.$store.dispatch('createMessageWithUndo', messagePayload);
        } else {
          await this.$store.dispatch(
            'createPendingMessageAndSend',
            messagePayload
          );
        }
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        this.removeFromDraft();
        if (wasSent) {
          emitter.emit(BUS_EVENTS.MESSAGE_SENT);
          this.sendMessageAnalyticsData(messagePayload.private, messagePayload);
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    // NOTICE: KAPTION-INTEGRATION - whatsapp-bsuid-coexistence.PHONE_REQUEST.2
    // Use the dedicated API so the persisted marker selects Meta's native
    // request_contact_info payload in the WhatsApp Cloud provider.
    async sendPhoneRequest(message) {
      try {
        const response =
          await KaptionContactInfoRequestsAPI.createContactInfoRequest(
            this.currentAccountId,
            this.currentChat.id,
            { body: message }
          );
        this.$store.dispatch('addMessage', response.data);
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
        throw error;
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    replaceText(payload) {
      // Support both object format { text, attachments } and legacy string format
      const message =
        typeof payload === 'object' && payload !== null
          ? payload.text
          : payload;
      const attachments =
        typeof payload === 'object' && payload !== null
          ? payload.attachments
          : null;

      let finalMessage = message;
      if (this.sendWithSignature && !this.private) {
        // if signature is enabled, append it to the message
        // appendSignature ensures that the signature is not duplicated
        // so we don't need to check if the signature is already present
        finalMessage = appendSignature(finalMessage, this.signatureToApply);
      }

      const updatedMessage = replaceVariablesInMessage({
        message: finalMessage,
        variables: this.messageVariables,
      });

      setTimeout(() => {
        useTrack(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);

      // Handle canned response attachments if provided
      if (attachments?.length > 0) {
        this.handleCannedResponseAttachments(attachments);
      }
    },
    // NOTICE: KAPTION-INTEGRATION - ai-takeover.COMPOSER.1
    enterKaptionAssistantLock() {
      if (this.isEditingMessage) return;
      if (this.canWritePrivateNotes) this.replyType = REPLY_EDITOR_MODES.NOTE;
    },
    // ai-takeover.COMPOSER.2 — the same choice the conversation would open with.
    leaveKaptionAssistantLock() {
      if (this.isEditingMessage || !this.currentChat?.id) return;
      if (this.currentChat.can_reply) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else if (this.isAWhatsAppChannel && this.isMetaPaymentConfirmed) {
        this.replyType = REPLY_EDITOR_MODES.FOLLOWUP;
        this.fetchKaptionUtilityTemplates();
      }
      this.$nextTick(() => this.$refs.messageInput?.focus?.());
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      // NOTICE: KAPTION-INTEGRATION - ai-takeover.COMPOSER.1: only notes until someone takes over.
      if (this.isKaptionAssistantLock && mode !== REPLY_EDITOR_MODES.NOTE) {
        return;
      }
      // NOTICE: KAPTION-INTEGRATION - Also guard keyboard mode switching.
      if (
        mode === REPLY_EDITOR_MODES.REPLY &&
        this.isWhatsAppReplyWindowClosed
      ) {
        return;
      }
      if (mode === REPLY_EDITOR_MODES.NOTE && !this.canWritePrivateNotes) {
        return;
      }

      // Clear attachments when switching between private note and reply modes
      // This is to prevent from breaking the upload rules
      if (this.attachedFiles.length > 0) this.attachedFiles = [];
      // NOTICE: KAPTION-INTEGRATION - Reset the @ switch flag when changing modes
      // (it will be set to true by the @ press handlers if needed)
      this.switchedViaAtPress = false;

      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel) this.replyType = mode;

      // NOTICE: KAPTION-INTEGRATION - Fetch templates and set default type based on mode
      if (
        mode === REPLY_EDITOR_MODES.SCHEDULE ||
        mode === REPLY_EDITOR_MODES.FOLLOWUP
      ) {
        // If templates are already loaded, just update the template type
        if (Object.keys(this.kaptionUtilityTemplates).length > 0) {
          const preferScheduleTypes = mode === REPLY_EDITOR_MODES.SCHEDULE;
          this.followupTemplateType =
            this.getFirstAvailableTemplateType(preferScheduleTypes);
        }
        // Fetch templates (will set type if not already loaded)
        this.fetchKaptionUtilityTemplates();
      }

      if (this.showRichContentEditor) {
        if (this.isRecordingAudio) {
          this.toggleAudioRecorder();
        }
        return;
      }
      this.$nextTick(() => this.$refs.messageInput.focus());
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    insertIntoTextEditor(text, selectionStart, selectionEnd) {
      const { message } = this;
      const newMessage =
        message.slice(0, selectionStart) +
        text +
        message.slice(selectionEnd, message.length);
      this.message = newMessage;
    },
    // NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ownership.OUTBOUND.3:
    // "Ask <name>" from the call button opens a private note that mentions
    // the assignee ({conversationId, content} on the bus).
    kaptionAskTeammate({ conversationId, content } = {}) {
      if (
        conversationId &&
        Number(conversationId) !== Number(this.conversationIdByRoute)
      )
        return;
      if (!this.canWritePrivateNotes || !content) return;
      this.replyType = REPLY_EDITOR_MODES.NOTE;
      this.$nextTick(() => {
        this.message = this.message ? `${this.message} ${content}` : content;
        this.onFocus();
      });
    },
    addIntoEditor(content) {
      if (this.showRichContentEditor) {
        this.updateEditorSelectionWith = content;
        this.onFocus();
      }
      if (!this.showRichContentEditor) {
        const { selectionStart, selectionEnd } = this.$refs.messageInput.$el;
        this.insertIntoTextEditor(content, selectionStart, selectionEnd);
      }
    },
    clearMessage() {
      this.message = '';
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        this.message = appendSignature(this.message, this.signatureToApply);
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    // NOTICE: KAPTION-INTEGRATION - Edit message mode methods
    enterEditMode({ id, content, conversationId }) {
      if (conversationId !== this.currentChat?.id) return;
      // Save current draft before entering edit mode
      this.saveDraft(this.conversationIdByRoute, this.replyType);
      // Set edit mode state BEFORE changing replyType so the replyType
      // watcher skips getFromDraft() and doesn't overwrite edit content
      this.isEditingMessage = true;
      this.editingMessageId = id;
      this.editingMessageOriginalContent = content;
      // Switch to private note mode since only notes can be edited
      this.replyType = REPLY_EDITOR_MODES.NOTE;
      this.message = content;
      // Focus the editor
      this.$nextTick(() => {
        if (this.showRichContentEditor) {
          this.onFocus();
        } else if (this.$refs.messageInput) {
          this.$refs.messageInput.$el.focus();
        }
      });
    },
    exitEditMode() {
      this.isEditingMessage = false;
      this.editingMessageId = null;
      this.editingMessageOriginalContent = '';
      this.message = '';
      // Restore draft
      this.getFromDraft();
    },
    async saveEditedMessage() {
      const trimmedMessage = trimContent(this.message || '');
      if (
        !trimmedMessage ||
        trimmedMessage === this.editingMessageOriginalContent
      ) {
        this.exitEditMode();
        return;
      }
      try {
        await this.$store.dispatch('editMessage', {
          conversationId: this.currentChat.id,
          messageId: this.editingMessageId,
          content: trimmedMessage,
        });
        useAlert(this.$t('CONVERSATION.EDIT_MESSAGE.SUCCESS'));
        this.exitEditMode();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.EDIT_MESSAGE.ERROR'));
      }
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    toggleEmojiPicker() {
      // feature-permissions.REPLY_BOX.7
      if (!this.canUseFeature('emoji_picker')) {
        this.showEmojiPicker = false;
        return;
      }
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      this.isRecorderAudioStopped = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.isRecordingAudio) {
        return;
      }
      if (!this.isRecorderAudioStopped) {
        this.isRecorderAudioStopped = true;
        this.$refs.audioRecorderInput.stopRecording();
      } else if (this.isRecorderAudioStopped) {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    hideMentions() {
      this.showMentions = false;
    },
    // NOTICE: KAPTION-INTEGRATION - Handle @ keypress in plain text editor
    // When @ is pressed as FIRST character in REPLY mode, switch to NOTE mode and show mentions
    // Only triggers when message is empty to avoid breaking email addresses
    onTextAreaKeydown(event) {
      // Only handle @ key when in REPLY mode (not already in NOTE mode)
      // AND only when the message is empty (@ is the first character)
      if (
        event.key === '@' &&
        this.canWritePrivateNotes &&
        this.replyType === REPLY_EDITOR_MODES.REPLY &&
        !this.isOnPrivateNote &&
        this.message.trim() === ''
      ) {
        // Prevent the @ from being typed in the reply textarea
        event.preventDefault();

        // Clear the message (we're starting fresh in private note)
        this.message = '';

        // Switch to private note mode
        this.setReplyMode(REPLY_EDITOR_MODES.NOTE);
        // Track that we switched via @ press for potential backspace return
        this.switchedViaAtPress = true;

        // After mode switch, insert @ into the rich editor
        // Use setTimeout to ensure the rich editor is fully mounted
        setTimeout(() => {
          // Emit event to insert @ into the rich editor
          // This triggers the prosemirror suggestions plugin to show mentions
          emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, '@');
        }, 100);
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Handle @ key press in rich editor when in Reply mode
    // This is called by the WootMessageEditor when @ key is pressed (not on draft restore)
    // Only triggers when message is empty (@ is first character) to avoid breaking email addresses
    onAtKeyPressedInRichEditor() {
      // Only handle when in REPLY mode (not already in NOTE mode)
      // AND only when the message is empty (@ is the first character)
      if (
        this.canWritePrivateNotes &&
        this.replyType === REPLY_EDITOR_MODES.REPLY &&
        !this.isOnPrivateNote &&
        this.message.trim() === ''
      ) {
        // Switch to private note mode - the @ will be typed into the new editor
        this.setReplyMode(REPLY_EDITOR_MODES.NOTE);
        // Track that we switched via @ press for potential backspace return
        this.switchedViaAtPress = true;
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Handle backspace removing @ to switch back to Reply mode
    onBackspaceRemovesAt() {
      // Only switch back if we previously switched via @ press
      if (this.switchedViaAtPress && this.isOnPrivateNote) {
        this.message = '';
        this.setReplyMode(REPLY_EDITOR_MODES.REPLY);
        this.switchedViaAtPress = false;
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Toolbar [@] icon click in Notes mode.
    // Inserts `@` at the cursor of the rich editor, which triggers the
    // prosemirror suggestions plugin and shows the agent picker.
    onInsertMentionFromToolbar() {
      if (!this.isOnPrivateNote) return;
      // Insert `@` via the same bus event used by the @-key auto-switch flow.
      // The editor is mounted on the next tick after the focus/select cycle.
      this.$nextTick(() => {
        emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, '@');
      });
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isRecordedAudio to the file to find it's and recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isRecordedAudio: true,
      };
      return file && this.onFileUpload(autoRecordedFile);
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
          isRecordedAudio: file?.isRecordedAudio || false,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    // NOTICE: KAPTION-INTEGRATION - Handle attachment injection from dashboard apps (copy-to-composer)
    handleAttachmentInjection(attachments) {
      if (!attachments || !Array.isArray(attachments)) {
        return;
      }

      // Process all attachments using array methods (ESLint compliant)
      attachments
        .filter(att => att.data && att.mimeType && att.title)
        .forEach(attachment => {
          try {
            // Decode base64 string to binary
            const binaryString = atob(attachment.data);
            const bytes = new Uint8Array(binaryString.length);
            // Use standard for loop for typed array assignment
            let i = 0;
            while (i < binaryString.length) {
              bytes[i] = binaryString.charCodeAt(i);
              i += 1;
            }
            const blob = new Blob([bytes], { type: attachment.mimeType });

            // Create a File object from the blob
            const file = new File([blob], attachment.title, {
              type: attachment.mimeType,
            });

            // Use the existing onFileUpload method to handle the file
            this.onFileUpload({
              name: file.name,
              type: file.type,
              size: file.size,
              file,
            });
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
              '[ReplyBox] Failed to inject attachment:',
              attachment.title,
              error
            );
          }
        });
    },
    // Handle attachments from canned responses by fetching files from URLs
    handleCannedResponseAttachments(attachments) {
      if (!attachments?.length) return;

      const fetchAndUpload = async attachment => {
        try {
          // Rewrite the URL to use the current origin — the server may return
          // a Docker-internal hostname (e.g. host.docker.internal) that the
          // browser cannot resolve.
          let fileUrl = attachment.file_url;
          try {
            const parsed = new URL(fileUrl);
            parsed.protocol = window.location.protocol;
            parsed.host = window.location.host;
            fileUrl = parsed.toString();
          } catch {
            // keep original URL if parsing fails
          }

          const response = await fetch(fileUrl, {
            credentials: 'include',
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const blob = await response.blob();
          const file = new File([blob], attachment.filename, {
            type: attachment.file_type || blob.type,
          });

          this.onFileUpload({
            name: file.name,
            type: file.type,
            size: file.size,
            file,
          });
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            '[ReplyBox] Failed to fetch canned attachment:',
            attachment.filename,
            error
          );
          useAlert(this.$t('CONVERSATION.CANNED_ATTACHMENT_ERROR'));
        }
      };

      attachments.forEach(attachment => fetchAndUpload(attachment));
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    // NOTICE: KAPTION-INTEGRATION
    // Shared helper to apply translation to message payloads
    applyTranslationToPayload(payload, originalMessage) {
      // Get translation if available
      const liveTranslationRef = this.$refs.liveTranslation;
      const translatedText = liveTranslationRef?.translatedText;
      const targetLang =
        liveTranslationRef?.targetLanguage?.value ||
        liveTranslationRef?.targetLanguage ||
        this.liveTranslationTargetLanguage;

      // If we have a translation and should translate
      if (
        this.shouldTranslateLiveReply &&
        translatedText &&
        translatedText.trim().length > 0
      ) {
        // Remove signature from original message
        const originalMessageWithoutSignature = removeSignature(
          originalMessage,
          this.signatureToApply
        );

        // Add translation metadata to payload
        // Store BOTH the original (in agent's typing language) AND the translation (in customer's language)
        // NOTICE: KAPTION-INTEGRATION - translation payload reversed as per fix request
        // NOTICE: KAPTION-INTEGRATION - contextual-translation.LIVE_REPLY.2
        // Persist the engine's REAL provider + context_aware (reported by the live
        // endpoint) instead of a hard-coded "kaption_live_reply / context_aware:false".
        // The hard-coded label is what produced the residual context_aware:false leak:
        // a context-aware (v2) reply was being mislabeled on the way to the message.
        // Fall back to the legacy label only if the endpoint reported nothing.
        const translations = {
          [this.liveTranslationSourceLanguage]: {
            text: originalMessageWithoutSignature,
            source_language: targetLang,
            target_language: this.liveTranslationSourceLanguage,
            confidence: 1.0,
            provider:
              liveTranslationRef?.translationProvider || 'kaption_live_reply',
            context_aware: liveTranslationRef?.translationContextAware ?? false,
            translated_at: new Date().toISOString(),
          },
        };

        return {
          ...payload,
          message: payload.message ? translatedText : payload.message,
          contentAttributes: {
            ...payload.contentAttributes,
            kaption_translations: translations,
          },
        };
      }

      return payload;
    },
    // NOTICE: KAPTION-INTEGRATION
    // Wrapper to add translation to multiple messages (WhatsApp/Instagram)
    getMultipleMessagesPayloadWithTranslation(message) {
      const messages = this.getMultipleMessagesPayload(message);

      // Apply translation to each message payload using the shared helper
      return messages.map(payload =>
        this.applyTranslationToPayload(payload, message)
      );
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        let caption = this.isAnInstagramChannel ? '' : message;
        this.attachedFiles.forEach((attachment, index) => {
          const attachedFile = this.globalConfig.directUploadsEnabled
            ? attachment.blobSignedId
            : attachment.resource.file;
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            private: false,
            message: caption,
            sender: this.sender,
            contentAttributes: { sequential_position: index },
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          multipleMessagePayload.push(attachmentPayload);
          // For WhatsApp, only the first attachment gets a caption
          if (!this.isAnInstagramChannel) caption = '';
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // For Instagram, we need a separate text message
      // For WhatsApp, we only need a text message if there are no attachments
      if (
        (this.isAnInstagramChannel && this.message) ||
        (!this.isAnInstagramChannel && hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
          contentAttributes: {
            sequential_position: multipleMessagePayload.length,
          },
        };

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      let messagePayload = {
        conversationId: this.currentChat.id,
        message,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }

      return messagePayload;
    },
    // NOTICE: KAPTION-INTEGRATION
    // Get message payload with live translation metadata if available
    getMessagePayloadWithTranslation(message) {
      const basePayload = this.getMessagePayload(message);

      // Apply translation using the shared helper
      return this.applyTranslationToPayload(basePayload, message);
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });

      // NOTICE: KAPTION-INTEGRATION
      // Auto-switch to private note mode when replying to a private note
      if (this.inReplyTo?.private && this.canWritePrivateNotes) {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    // NOTICE: KAPTION-INTEGRATION
    // Handle conversation language change from TranslationToggle picker
    async handleConversationLanguageChange({ conversationId, language }) {
      if (conversationId !== this.currentChat?.id) {
        return;
      }

      try {
        // Update conversation additional_attributes using new action
        await this.$store.dispatch('updateAdditionalAttributes', {
          conversationId,
          additionalAttributes: {
            conversation_language: language,
          },
        });

        // Refresh the conversation to get updated data
        await this.$store.dispatch('getConversation', {
          conversationId,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          '[ReplyBox] Failed to update conversation language:',
          error
        );
      }
    },
    // NOTICE: KAPTION-INTEGRATION
    // Handle translation toggle event from KaptionAIFeaturesPill
    handleTranslationToggle({ conversationId, enabled }) {
      if (conversationId === this.currentChat?.id) {
        // Update the composable's ref to maintain reactivity
        this.setTranslation(enabled);
      }
    },
    // NOTICE: KAPTION-INTEGRATION
    // Handle suggestions toggle event from KaptionAIFeaturesPill
    handleSuggestionsToggle({ conversationId, enabled }) {
      if (conversationId === this.currentChat?.id) {
        // Update the composable's ref to maintain reactivity
        this.setSuggestions(enabled);
      }
    },
    // NOTICE: KAPTION-INTEGRATION
    // NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging: Deshacer / Esc
    // puts the last held text back, ahead of anything already being typed.
    // Paused because the customer replied → the whole merged text comes back.
    undoKaptionMergedFragment() {
      const conversationId = this.currentChat?.id;
      const fragment = this.kaptionMergeGroup?.paused
        ? this.kaptionMerge.takeBack(conversationId)
        : this.kaptionMerge.undoLast(conversationId);
      if (!fragment) return;
      const typed = this.message?.trim() ? this.message : '';
      this.message = typed ? `${fragment.content}\n${typed}` : fragment.content;
      this.$nextTick(() => this.$refs.messageInput?.focus?.());
    },
    // Handle undo restore content event - restores message content when user undoes a pending send
    handleUndoRestoreContent({ content, attachments, conversationId }) {
      // Only restore if this is the same conversation
      if (conversationId !== this.currentChat?.id) return;

      // Restore content to the editor
      if (content) {
        this.message = content;
      }

      // Restore attachments if any
      if (attachments && attachments.length > 0) {
        this.attachedFiles = attachments;
      }

      // Focus the editor
      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.focus();
        }
      });
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    // NOTICE: KAPTION-INTEGRATION - Initialize follow-up mode on component mount if needed
    initializeFollowupModeIfNeeded() {
      // eslint-disable-next-line no-console
      console.log('[KAPTION] initializeFollowupModeIfNeeded', {
        currentChat: this.currentChat?.id,
        inbox: this.inbox,
        inboxId: this.inboxId,
        channelType: this.channelType,
        isAWhatsAppChannel: this.isAWhatsAppChannel,
        canReply: this.currentChat?.can_reply,
        currentReplyType: this.replyType,
        metaPaymentConfirmed: this.isMetaPaymentConfirmed,
      });

      // NOTICE: KAPTION-INTEGRATION - template-languages.UI.2: Seguimiento and
      // Programar open on the account's language when it offers it, else the
      // first offered language (no blind fallback to 'en'). The contact's
      // detected language is not used; the agent switches with the flag.
      this.followupLanguage = defaultTemplateLanguage({
        accountLocale: this.getAccount(this.currentAccountId)?.locale,
        allowed: this.allowedTemplateLanguages,
      });

      // Check if this is a WhatsApp channel with expired 24h window AND payment is confirmed
      if (
        this.isAWhatsAppChannel &&
        !this.currentChat?.can_reply &&
        this.isMetaPaymentConfirmed
      ) {
        // eslint-disable-next-line no-console
        console.log(
          '[KAPTION] WhatsApp with can_reply=false and payment confirmed detected on mount, setting FOLLOWUP mode'
        );
        this.replyType = REPLY_EDITOR_MODES.FOLLOWUP;
        this.fetchKaptionUtilityTemplates();
      } else if (this.currentChat && !this.inbox) {
        // Inbox might not be loaded yet, wait and retry
        // eslint-disable-next-line no-console
        console.log('[KAPTION] Inbox not loaded yet, will retry on watch');
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Fetch utility templates for follow-up mode
    async fetchKaptionUtilityTemplates() {
      // eslint-disable-next-line no-console
      console.log('[KAPTION] fetchKaptionUtilityTemplates called', {
        isLoadingTemplates: this.isLoadingTemplates,
        inboxId: this.inboxId,
        hasTemplates: Object.keys(this.kaptionUtilityTemplates).length > 0,
      });

      if (this.isLoadingTemplates || !this.inboxId) {
        // eslint-disable-next-line no-console
        console.log('[KAPTION] Skipping fetch - already loading or no inboxId');
        return;
      }

      // Check if templates are already loaded
      if (Object.keys(this.kaptionUtilityTemplates).length > 0) {
        // eslint-disable-next-line no-console
        console.log('[KAPTION] Templates already loaded, skipping fetch');
        return;
      }

      this.isLoadingTemplates = true;
      try {
        // eslint-disable-next-line no-console
        console.log(
          '[KAPTION] Fetching templates from API for inbox:',
          this.inboxId
        );

        const response = await KaptionUtilityTemplatesAPI.getTemplates(
          this.inboxId,
          'followup'
        );

        // eslint-disable-next-line no-console
        console.log('[KAPTION] API response:', response.data);

        // API returns { templates: {...}, available_languages: [...], ... }
        // We need the templates object
        this.kaptionUtilityTemplates = response.data?.templates || {};

        // eslint-disable-next-line no-console
        console.log(
          '[KAPTION] Parsed templates:',
          this.kaptionUtilityTemplates
        );

        // NOTICE: KAPTION-INTEGRATION - Auto-detect first available template type
        // IMPORTANT: Only look at templates in the ACCOUNT LANGUAGE - do NOT fall back to other languages
        // For Schedule mode, prefer scheduled/reminder types; for Follow-up mode, prefer followup type
        const lang = this.followupLanguage || 'en';
        const preferScheduleTypes = this.isOnScheduleMode;
        // eslint-disable-next-line no-console
        console.log(
          '[KAPTION] Account language:',
          lang,
          'preferScheduleTypes:',
          preferScheduleTypes
        );

        const availableType =
          this.getFirstAvailableTemplateType(preferScheduleTypes);
        // eslint-disable-next-line no-console
        console.log(
          '[KAPTION] First available template type in',
          lang,
          ':',
          availableType
        );
        this.followupTemplateType = availableType;

        // NOTICE: KAPTION-INTEGRATION - Auto-select first template ONLY from account language
        // Do NOT fall back to other languages - if no templates in account language, show error state
        const typeTemplates = this.kaptionUtilityTemplates[availableType];
        const templates = typeTemplates?.[lang] || [];

        // eslint-disable-next-line no-console
        console.log('[KAPTION] Templates for type/language:', {
          type: availableType,
          lang,
          count: templates.length,
        });

        if (templates.length > 0) {
          this.selectedFollowupTemplate = templates[0];
          // eslint-disable-next-line no-console
          console.log(
            '[KAPTION] Auto-selected template:',
            this.selectedFollowupTemplate
          );
        } else {
          // No templates in account language - error state will show
          this.selectedFollowupTemplate = null;
          // eslint-disable-next-line no-console
          console.warn(
            '[KAPTION] No templates found in account language:',
            lang
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[KAPTION] Failed to fetch utility templates:', error);
        // eslint-disable-next-line no-console
        console.error(
          '[KAPTION] Error details:',
          error.response?.data || error.message
        );
      } finally {
        this.isLoadingTemplates = false;
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Get templates for current type and language
    // ONLY returns templates in the specified language - NO fallback to other languages
    getFollowupTemplatesForLanguage() {
      const lang = this.followupLanguage || 'en';
      const typeTemplates =
        this.kaptionUtilityTemplates[this.followupTemplateType];
      if (!typeTemplates) return [];
      return typeTemplates[lang] || [];
    },
    // NOTICE: KAPTION-INTEGRATION - Find first template type that has templates IN THE ACCOUNT LANGUAGE ONLY
    // For Schedule mode, prefer 'scheduled' or 'reminder' types first
    // feature-permissions.REPLY_BOX.8 - 'reminder' excluded unless permitted
    getFirstAvailableTemplateType(preferScheduleTypes = false) {
      // Order types based on current mode:
      // - Schedule mode: prefer scheduled/reminder first
      // - Follow-up mode: prefer followup first
      const remindersAllowed = this.permittedTemplateTypes.includes('reminder');
      let types = preferScheduleTypes
        ? ['scheduled', 'reminder', 'followup']
        : ['followup', 'scheduled', 'reminder'];
      if (!remindersAllowed) {
        types = types.filter(type => type !== 'reminder');
      }
      const lang = this.followupLanguage || 'en';

      // ONLY check for templates in the account language - do NOT fall back to other languages
      const foundType = types.find(type => {
        const typeTemplates = this.kaptionUtilityTemplates[type];
        return typeTemplates?.[lang]?.length > 0;
      });

      // No templates in account language - return first type as default (error state will show)
      return foundType || types[0];
    },
    // NOTICE: KAPTION-INTEGRATION - Get template count for a type
    getTemplateCountForType(type) {
      const typeTemplates = this.kaptionUtilityTemplates[type];
      if (!typeTemplates) return 0;
      return Object.values(typeTemplates).reduce(
        (sum, arr) => sum + (arr?.length || 0),
        0
      );
    },
    // NOTICE: KAPTION-INTEGRATION - Handle template type change
    // ONLY selects templates in account language - NO fallback to other languages
    handleTemplateTypeChange(newType) {
      this.followupTemplateType = newType;
      // Auto-select first template in new type, but ONLY in account language
      const typeTemplates = this.kaptionUtilityTemplates[newType];
      const lang = this.followupLanguage || 'en';
      const templates = typeTemplates?.[lang] || [];

      if (templates.length > 0) {
        this.selectedFollowupTemplate = templates[0];
      } else {
        // No templates in account language for this type
        this.selectedFollowupTemplate = null;
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Handle attachment uploaded in utility template
    handleAttachmentUploaded(attachment) {
      // eslint-disable-next-line no-console
      console.log('[KAPTION] Attachment uploaded:', attachment);
      // Store the full attachment for sending with the template (url, hash, filename)
      this.templateAttachment = attachment;
      this.templateAttachmentHash = attachment.hash;
    },
    // NOTICE: KAPTION-INTEGRATION - Send follow-up template message
    async onSendFollowup() {
      if (!this.selectedFollowupTemplate || !this.followupVariable?.trim()) {
        useAlert(
          this.$t(
            'KAPTION_UTILITY_TEMPLATES.VARIABLE_REQUIRED',
            'Please enter a message'
          )
        );
        return;
      }

      try {
        // NOTICE: KAPTION-INTEGRATION
        // Build template_params in the format expected by TemplateProcessorService
        // The backend expects: { name, language, processed_params: { body: { "1": value } } }
        const templateName = this.selectedFollowupTemplate.name;
        // Extract language from template name (e.g., kaption_v2_followup_r1_att1_en -> en)
        const langMatch = templateName.match(/_([a-z]{2}(?:_[A-Z]{2})?)$/);
        const templateLanguage = langMatch
          ? langMatch[1]
          : this.followupLanguage || 'en';

        // Build the final message by replacing {{1}} with the variable (for display)
        const templateBody = this.selectedFollowupTemplate.body || '';
        const finalMessage = templateBody.replace(
          /\{\{1\}\}/g,
          this.followupVariable
        );

        // NOTICE: KAPTION-INTEGRATION
        // Send as a WhatsApp template message with proper template_params format
        // Note: The attachment URL is included in the body variable text, not as a header component
        // (template header attachments require templates with HEADER components defined in WhatsApp)
        const processedParams = {
          body: {
            1: this.followupVariable,
          },
        };

        const messagePayload = {
          conversationId: this.currentChat.id,
          message: finalMessage,
          private: false,
          sender: this.sender,
          // template_params goes in additional_attributes for WhatsApp template sending
          templateParams: {
            name: templateName,
            language: templateLanguage,
            processed_params: processedParams,
            // NOTICE: KAPTION-INTEGRATION - Include attachment_hash for expiry extension
            attachment_hash: this.templateAttachmentHash || null,
          },
        };

        await this.sendMessage(messagePayload);

        // Clear the variable and attachment after sending
        this.followupVariable = '';
        this.templateAttachment = null;
        this.templateAttachmentHash = null;

        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Schedule message handler with translation support
    async onScheduleMessage(scheduleData) {
      // NOTICE: KAPTION-INTEGRATION - reply-template-picker.SCHEDULE.1
      if (this.isPickedTemplateActive) {
        await this.onSchedulePickedTemplate(scheduleData);
        return;
      }
      const { scheduledAt, timezone } = scheduleData;

      try {
        let payload = {
          scheduled_at: scheduledAt,
          timezone: timezone,
        };

        if (this.isOnFollowupMode || this.isOnScheduleMode) {
          // Schedule a template message (for both Follow-up and Schedule modes)
          if (
            !this.selectedFollowupTemplate ||
            !this.followupVariable?.trim()
          ) {
            useAlert(
              this.$t(
                'KAPTION_UTILITY_TEMPLATES.VARIABLE_REQUIRED',
                'Please enter a message'
              )
            );
            return;
          }

          payload.message_type = 'template';
          payload.template_name = this.selectedFollowupTemplate.name;
          payload.variables = { 1: this.followupVariable };
          // NOTICE: KAPTION-INTEGRATION - Include attachment hash for scheduled template messages
          if (this.templateAttachmentHash) {
            payload.attachment_hash = this.templateAttachmentHash;
          }
        } else {
          // Schedule a regular message
          if (!this.message?.trim()) {
            useAlert(
              this.$t(
                'CONVERSATION.SCHEDULE_MESSAGE.MESSAGE_REQUIRED',
                'Please enter a message'
              )
            );
            return;
          }

          payload.message_type = 'regular';

          // NOTICE: KAPTION-INTEGRATION - Include translation if live translation is enabled
          const liveTranslationRef = this.$refs.liveTranslation;
          const translatedText = liveTranslationRef?.translatedText;

          if (
            this.shouldTranslateLiveReply &&
            translatedText &&
            translatedText.trim().length > 0
          ) {
            // Use translated text as the message content
            payload.message_content = translatedText;

            // Store original message and translation metadata
            const originalMessageWithoutSignature = removeSignature(
              this.message,
              this.signatureToApply
            );

            // NOTICE: KAPTION-INTEGRATION - contextual-translation.LIVE_REPLY.2
            // Carry the real engine label + context_aware here too (scheduled-send
            // path), mirroring the immediate-send path above.
            payload.translation_metadata = {
              original_text: originalMessageWithoutSignature,
              translated_text: translatedText,
              source_language: this.liveTranslationSourceLanguage,
              target_language: this.liveTranslationTargetLanguage,
              provider:
                liveTranslationRef?.translationProvider || 'kaption_live_reply',
              context_aware:
                liveTranslationRef?.translationContextAware ?? false,
            };
          } else {
            // No translation - use original message
            payload.message_content = this.message;
          }
        }

        await KaptionScheduledMessagesAPI.createScheduledMessage(
          this.currentAccountId,
          this.currentChat.id,
          payload
        );

        // NOTICE: KAPTION-INTEGRATION - Emit event to notify other components (like KaptionAIFeaturesPill)
        emitter.emit('kaption-message-scheduled', {
          conversationId: this.currentChat.id,
        });

        useAlert(
          this.$t(
            'CONVERSATION.SCHEDULE_MESSAGE.SUCCESS',
            'Message scheduled successfully'
          )
        );

        // Clear the message after scheduling
        if (this.isOnFollowupMode || this.isOnScheduleMode) {
          this.followupVariable = '';
        } else {
          this.clearMessage();
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.errors?.[0] ||
          error?.response?.data?.error ||
          this.$t(
            'CONVERSATION.SCHEDULE_MESSAGE.ERROR',
            'Failed to schedule message'
          );
        useAlert(errorMessage);
      }
    },
    // NOTICE: KAPTION-INTEGRATION - reply-template-picker (PICKER / EDITOR / SEND / SCHEDULE)
    openReplyTemplatePicker() {
      if (!this.showReplyTemplateButton) return;
      this.showReplyTemplateModal = true;
    },
    closeReplyTemplatePicker() {
      this.showReplyTemplateModal = false;
    },
    onReplyTemplatePicked(template) {
      this.pickedTemplate = template;
      this.pickedTemplateState = {
        bodyValues: {},
        headerMedia: {},
        buttonValues: {},
        isValid: false,
      };
      this.showReplyTemplateModal = false;
    },
    onPickedTemplateChange(state) {
      this.pickedTemplateState = state;
    },
    clearPickedTemplate() {
      this.pickedTemplate = null;
      this.showReplyTemplateModal = false;
      this.pickedTemplateState = {
        bodyValues: {},
        headerMedia: {},
        buttonValues: {},
        isValid: false,
      };
    },
    // reply-template-picker.SEND.2 — a template Meta has not approved yet is
    // queued as an approval-gated scheduled message due now (#335), exactly
    // like the new-conversation wizard.
    async queuePickedTemplateForApproval() {
      const payload = buildPickedTemplateSchedulePayload({
        template: this.pickedTemplate,
        ...this.pickedTemplateState,
        scheduledAt: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      });
      await KaptionScheduledMessagesAPI.createScheduledMessage(
        this.currentAccountId,
        this.currentChat.id,
        payload
      );
      emitter.emit('kaption-message-scheduled', {
        conversationId: this.currentChat.id,
      });
      useAlert(this.$t('KAPTION_REPLY_TEMPLATE.QUEUED_FOR_APPROVAL'));
    },
    // reply-template-picker.SEND.1 — same path as Seguimiento: sendMessage()
    // with templateParams, behind the same closed-window guard.
    async onSendPickedTemplate() {
      if (!this.pickedTemplate || !this.pickedTemplateState.isValid) return;
      if (this.isSendingPickedTemplate) return;
      this.isSendingPickedTemplate = true;
      try {
        if (this.isPickedTemplatePending) {
          await this.queuePickedTemplateForApproval();
        } else {
          await this.sendMessage(
            buildPickedTemplateMessagePayload({
              conversationId: this.currentChat.id,
              template: this.pickedTemplate,
              ...this.pickedTemplateState,
              sender: this.sender,
            })
          );
        }
        this.clearPickedTemplate();
      } catch (error) {
        useAlert(
          error?.response?.data?.errors?.[0] ||
            error?.response?.data?.error ||
            this.$t('CONVERSATION.MESSAGE_ERROR')
        );
      } finally {
        this.isSendingPickedTemplate = false;
      }
    },
    // reply-template-picker.SCHEDULE.1 — the Programar popover, same API and
    // payload as the wizard's scheduleLater.
    async onSchedulePickedTemplate({ scheduledAt, timezone }) {
      if (!this.pickedTemplate || !this.pickedTemplateState.isValid) return;
      try {
        await KaptionScheduledMessagesAPI.createScheduledMessage(
          this.currentAccountId,
          this.currentChat.id,
          buildPickedTemplateSchedulePayload({
            template: this.pickedTemplate,
            ...this.pickedTemplateState,
            scheduledAt,
            timezone,
          })
        );
        emitter.emit('kaption-message-scheduled', {
          conversationId: this.currentChat.id,
        });
        useAlert(
          this.$t(
            'CONVERSATION.SCHEDULE_MESSAGE.SUCCESS',
            'Message scheduled successfully'
          )
        );
        this.clearPickedTemplate();
      } catch (error) {
        useAlert(
          error?.response?.data?.errors?.[0] ||
            error?.response?.data?.error ||
            this.$t(
              'CONVERSATION.SCHEDULE_MESSAGE.ERROR',
              'Failed to schedule message'
            )
        );
      }
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    // NOTICE: KAPTION-INTEGRATION - Follow-up template control methods
    handleFollowupLanguageChange(lang) {
      this.followupLanguage = lang;
      // Delegate to the template component
      this.utilityTemplateRef?.changeLanguage(lang);
    },
    handlePrevTemplate() {
      this.utilityTemplateRef?.goToPrev();
    },
    handleNextTemplate() {
      this.utilityTemplateRef?.goToNext();
    },
    // NOTICE: KAPTION-INTEGRATION - Trigger file picker in utility template editor
    handleTriggerAttachment() {
      this.utilityTemplateRef?.triggerFilePicker();
    },
    handleToggleTemplateSearch() {
      this.showTemplateSearch = !this.showTemplateSearch;
    },
    handleTemplateSearchClose() {
      this.showTemplateSearch = false;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.hasRecordedAudio = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isRecordedAudio
      );
    },
    togglePopout() {
      this.$emit('update:popOutReplyBox', !this.popOutReplyBox);
    },
  },
};
</script>

<template>
  <ReplyBoxBanner :message="message" :is-on-private-note="isOnPrivateNote" />
  <!-- NOTICE: KAPTION-INTEGRATION - ai-takeover.COMPOSER.1 -->
  <KaptionAiTakeoverStrip
    v-if="isKaptionAssistantLock && !isEditingMessage"
    :conversation="currentChat"
  />
  <div ref="replyEditor" class="reply-box" :class="replyBoxClass">
    <!-- NOTICE: KAPTION-INTEGRATION - Pass WhatsApp channel, canReply, isFollowupAvailable and showScheduleTab props -->
    <ReplyTopPanel
      :mode="replyType"
      :is-reply-restricted="isReplyRestricted || isKaptionAssistantLock"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :popout-reply-box="popOutReplyBox"
      :is-whats-app-channel="isAWhatsAppChannel"
      :can-reply="currentChat?.can_reply"
      :is-followup-available="isFollowupAvailable"
      :show-schedule-tab="showScheduleTab"
      :show-private-note="canWritePrivateNotes"
      :is-editing-message="isEditingMessage"
      :show-template-button="showReplyTemplateButton"
      :is-template-loaded="isPickedTemplateActive"
      @set-reply-mode="setReplyMode"
      @toggle-popout="togglePopout"
      @cancel-edit="exitEditMode"
      @open-template-picker="openReplyTemplatePicker"
    />
    <!-- NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging -->
    <KaptionPendingSendStrip
      v-if="kaptionMergeGroup && !isOnPrivateNote"
      :group="kaptionMergeGroup"
      :composer-has-text="!isMessageEmpty"
      @undo="undoKaptionMergedFragment"
    />
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <!-- NOTICE: KAPTION-INTEGRATION - Follow-up and Schedule Mode UI -->
    <!-- NOTICE: KAPTION-INTEGRATION - reply-template-picker.EDITOR.1 - a picked
         template uses the follow-up area in Responder, Seguimiento and Programar -->
    <div
      v-if="isOnFollowupMode || isOnScheduleMode || isPickedTemplateActive"
      class="reply-box__top followup-mode"
    >
      <KaptionPickedTemplateEditor
        v-if="isPickedTemplateActive"
        :key="`${pickedTemplate.name}-${pickedTemplate.language}`"
        :template="pickedTemplate"
        :contact="currentChat?.meta?.sender"
        :disabled="isSendingPickedTemplate"
        @change="onPickedTemplateChange"
      />
      <!-- Error State: No templates in account language -->
      <div v-else-if="followupNoTemplatesError" class="followup-error-state">
        <div class="error-icon">
          <span class="i-lucide-alert-triangle size-8 text-n-amber-9" />
        </div>
        <h3 class="error-title">
          {{ $t('KAPTION_UTILITY_TEMPLATES.NO_TEMPLATES_IN_LANGUAGE') }}
        </h3>
        <p class="error-message">
          {{
            $t(
              'KAPTION_UTILITY_TEMPLATES.NO_TEMPLATES_IN_LANGUAGE_DESC',
              'There are no utility templates available for {language}. Please sync templates or select a different language.',
              { language: followupLanguage }
            )
          }}
        </p>
        <button class="browse-templates-btn" @click="showTemplateSearch = true">
          <span class="i-lucide-folder-search size-4" />
          {{ $t('KAPTION_UTILITY_TEMPLATES.BROWSE_ALL_TEMPLATES') }}
        </button>
      </div>
      <!-- Normal Template Editor -->
      <KaptionUtilityTemplateReplyBox
        v-else
        ref="utilityTemplateRef"
        v-model:template="selectedFollowupTemplate"
        v-model:variable="followupVariable"
        :templates="kaptionUtilityTemplates"
        :template-type="followupTemplateType"
        :language="followupLanguage"
        :allowed-languages="allowedTemplateLanguages"
        :show-search="showTemplateSearch"
        :disabled="isLoadingTemplates"
        enable-attachments
        @update:template-type="handleTemplateTypeChange"
        @update:show-search="showTemplateSearch = $event"
        @attachment-uploaded="handleAttachmentUploaded"
      />
    </div>

    <!-- Normal Reply/Note Mode UI -->
    <div v-else class="reply-box__top">
      <!-- NOTICE: KAPTION-INTEGRATION -->
      <!-- Live translation preview -->
      <LiveReplyTranslation
        ref="liveTranslation"
        :conversation-id="conversationId"
        :reply-text="message"
        :source-language="liveTranslationSourceLanguage"
        :target-language="liveTranslationTargetLanguage"
        :enabled="shouldTranslateLiveReply"
      />
      <!-- AI Chat Suggestions Widget -->
      <!-- NOTICE: KAPTION-INTEGRATION - Suggestions are reply-mode only.
           Private notes are agent-internal and should never trigger an AI
           recommendation (also avoids the suggestion API call entirely). -->
      <KaptionChatSuggestions
        v-if="!isOnPrivateNote"
        :conversation-id="conversationId"
        :inbox-id="inboxId"
        :account-id="currentAccountId"
        @suggestion-selected="onSuggestionSelected"
      />
      <ReplyToMessage
        v-if="shouldShowReplyToMessage"
        :message="inReplyTo"
        @dismiss="resetReplyToMessage"
      />
      <!-- feature-permissions.REPLY_BOX.2 -->
      <CannedResponse
        v-if="
          showMentions && hasSlashCommand && canUseFeature('canned_responses')
        "
        class="normal-editor__canned-box"
        :search-key="mentionSearchKey"
        @replace="replaceText"
      />
      <EmojiInput
        v-if="showEmojiPicker"
        v-on-clickaway="hideEmojiPicker"
        :class="{
          'emoji-dialog--expanded': isOnExpandedLayout || popOutReplyBox,
        }"
        :on-click="addIntoEditor"
      />
      <ReplyEmailHead
        v-if="showReplyHead"
        v-model:cc-emails="ccEmails"
        v-model:bcc-emails="bccEmails"
        v-model:to-emails="toEmails"
      />
      <AudioRecorder
        v-if="showAudioRecorderEditor"
        ref="audioRecorderInput"
        :audio-record-format="audioRecordFormat"
        @recorder-progress-changed="onRecordProgressChanged"
        @finish-record="onFinishRecorder"
        @play="recordingAudioState = 'playing'"
        @pause="recordingAudioState = 'paused'"
      />
      <!-- NOTICE: KAPTION-INTEGRATION - Added keydown handler for @ mention auto-switch to private note -->
      <ResizableTextArea
        v-else-if="!showRichContentEditor"
        ref="messageInput"
        v-model="message"
        class="rounded-none input"
        :placeholder="messagePlaceHolder"
        :min-height="4"
        :signature="signatureToApply"
        allow-signature
        :send-with-signature="sendWithSignature"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onTextAreaKeydown"
      />
      <!-- NOTICE: KAPTION-INTEGRATION - Added @ key handlers for mention auto-switch -->
      <!-- NOTICE: KAPTION-INTEGRATION - Added hideMenuBar for WhatsApp formatting mode -->
      <WootMessageEditor
        v-else
        v-model="message"
        :editor-id="editorStateId"
        class="input"
        :is-private="isOnPrivateNote"
        :placeholder="messagePlaceHolder"
        :update-selection-with="updateEditorSelectionWith"
        :min-height="4"
        enable-variables
        :variables="messageVariables"
        :signature="signatureToApply"
        allow-signature
        :channel-type="channelType"
        :hide-menu-bar="shouldHideEditorMenuBar"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
        @toggle-user-mention="toggleUserMention"
        @toggle-canned-menu="toggleCannedMenu"
        @toggle-variables-menu="toggleVariablesMenu"
        @clear-selection="clearEditorSelection"
        @at-key-pressed="onAtKeyPressedInRichEditor"
        @backspace-on-at-only="onBackspaceRemovesAt"
        @canned-response-attachments="handleCannedResponseAttachments"
      />
    </div>
    <div
      v-if="hasAttachments && !showAudioRecorderEditor"
      class="attachment-preview-box"
      @paste="onPaste"
    >
      <AttachmentPreview
        class="flex-col mt-4"
        :attachments="attachedFiles"
        @remove-attachment="removeAttachment"
      />
    </div>
    <MessageSignatureMissingAlert
      v-if="isSignatureEnabledForInbox && !isSignatureAvailable"
    />
    <!-- NOTICE: KAPTION-INTEGRATION - Pass isOnFollowupMode, schedule props, and follow-up controls -->
    <ReplyBottomPanel
      :conversation-id="conversationId"
      :enable-multiple-file-upload="enableMultipleFileUpload"
      :enable-whats-app-templates="showWhatsappTemplates"
      :enable-content-templates="showContentTemplates"
      :inbox="inbox"
      :is-on-private-note="isOnPrivateNote"
      :is-on-followup-mode="isOnFollowupMode"
      :is-on-schedule-mode="isOnScheduleMode"
      :is-recording-audio="isRecordingAudio"
      :is-send-disabled="isReplyButtonDisabled"
      :is-note="isPrivate"
      :on-file-upload="onFileUpload"
      :on-send="onSendReply"
      :conversation-type="conversationType"
      :recording-audio-duration-text="recordingAudioDurationText"
      :recording-audio-state="recordingAudioState"
      :send-button-text="replyButtonLabel"
      :send-button-tooltip="replyButtonTooltip"
      :show-audio-recorder="showAudioRecorder"
      :show-editor-toggle="isAPIInbox && !isOnPrivateNote"
      :show-emoji-picker="showEmojiPicker"
      :show-file-upload="showFileUpload"
      :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
      :toggle-audio-recorder="toggleAudioRecorder"
      :toggle-emoji-picker="toggleEmojiPicker"
      :portal-slug="connectedPortalSlug"
      :new-conversation-modal-active="newConversationModalActive"
      :followup-language="followupLanguage"
      :followup-languages="followupAvailableLanguages"
      :can-go-prev-template="canGoPrevFollowupTemplate"
      :can-go-next-template="canGoNextFollowupTemplate"
      :template-counter="followupTemplateCounter"
      :show-template-search="showTemplateSearch"
      :show-schedule-button="showScheduleButton"
      :message-type="scheduleMessageType"
      :is-uploading-attachment="isUploadingAttachment"
      :show-whatsapp-formatting-toggle="showFormattingToolbarToggle"
      :is-whatsapp-formatting-enabled="isFormattingToolbarVisible"
      :phone-request-feature-enabled="phoneRequestFeatureEnabled"
      :phone-request-phone-number="currentContact?.phone_number || ''"
      :phone-request-has-bsuid-alias="phoneRequestHasBsuidAlias"
      :phone-request-outbound-recipient-enabled="
        phoneRequestOutboundRecipientEnabled
      "
      :phone-request-can-send-messages="phoneRequestCanSendMessages"
      :phone-request-can-reply="Boolean(currentChat?.can_reply)"
      :on-phone-request-send="sendPhoneRequest"
      :picked-template="isPickedTemplateActive ? pickedTemplate : null"
      @clear-picked-template="clearPickedTemplate"
      @toggle-whatsapp-formatting="toggleFormattingToolbar"
      @select-whatsapp-template="openWhatsappTemplateModal"
      @open-canned-response-browser="openCannedResponseBrowser"
      @select-content-template="openContentTemplateModal"
      @toggle-editor="toggleRichContentEditor"
      @replace-text="replaceText"
      @toggle-insert-article="toggleInsertArticle"
      @change-followup-language="handleFollowupLanguageChange"
      @prev-template="handlePrevTemplate"
      @next-template="handleNextTemplate"
      @toggle-template-search="handleToggleTemplateSearch"
      @schedule-message="onScheduleMessage"
      @trigger-attachment="handleTriggerAttachment"
      @insert-mention="onInsertMentionFromToolbar"
    />
    <WhatsappTemplates
      v-if="canUseFeature('whatsapp_templates')"
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />
    <CannedResponseBrowser
      v-if="canUseFeature('canned_responses')"
      :show="showCannedResponseBrowser"
      @insert="onInsertCannedResponse"
      @close="hideCannedResponseBrowser"
    />

    <!-- NOTICE: KAPTION-INTEGRATION - reply-template-picker.PICKER.1 -->
    <KaptionReplyTemplateModal
      v-if="showReplyTemplateModal && inbox"
      :inbox="inbox"
      :contact-name="pickedTemplateContactName"
      :selected-template="pickedTemplate"
      :conversation-language="liveTranslationTargetLanguage"
      @select="onReplyTemplatePicked"
      @close="closeReplyTemplatePicker"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.attachment-preview-box {
  @apply bg-transparent py-0 px-3;
}

.reply-box {
  transition: height 2s cubic-bezier(0.37, 0, 0.63, 1);

  /* NOTICE: KAPTION-INTEGRATION - More rounded corners per Kaption style guide */
  @apply relative mb-2 mx-2 border border-n-weak rounded-3xl bg-n-solid-1;

  &.is-private {
    @apply bg-n-solid-amber dark:border-n-amber-3/10 border-n-amber-12/5;
  }
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative py-0 px-3 -mt-px;

  textarea {
    @apply shadow-none outline-none border-transparent bg-transparent m-0 max-h-60 min-h-[3rem] pt-4 pb-0 px-1 resize-none;
  }
}

.emoji-dialog {
  @apply top-[unset] -bottom-10 ltr:-left-80 ltr:right-[unset] rtl:left-[unset] rtl:-right-80;

  &::before {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply ltr:-right-4 bottom-2 rtl:-left-4 ltr:rotate-[270deg] rtl:rotate-[90deg];
  }
}

.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}

.normal-editor__canned-box {
  width: calc(100% - 2 * 0.75rem);
  left: 0.75rem;
}

/* NOTICE: KAPTION-INTEGRATION - Follow-up mode styles */
.followup-mode {
  @apply py-0 px-0;

  :deep(.kaption-utility-template-reply-box) {
    @apply border-0 rounded-none;
  }
}

/* NOTICE: KAPTION-INTEGRATION - Error state for no templates */
.followup-error-state {
  @apply flex flex-col items-center justify-center gap-3 py-8 px-6;
  @apply bg-n-amber-3/20 border border-n-amber-6/30 rounded-lg mx-4 my-4;
  text-align: center;
}

.error-icon {
  @apply flex items-center justify-center;
}

.error-title {
  @apply text-base font-semibold text-n-amber-11;
}

.error-message {
  @apply text-sm text-n-slate-11 max-w-md;
}

.browse-templates-btn {
  @apply flex items-center gap-2 px-4 py-2 mt-2;
  @apply bg-n-slate-12 text-n-slate-1 rounded-lg;
  @apply text-sm font-medium;
  @apply hover:bg-n-slate-11 transition-colors;
}
</style>
